import React, {useCallback, useEffect, useRef, useState} from 'react';
import type {Logical} from 'lightweight-charts';
import {
    CandlestickSeries,
    ColorType,
    createChart,
    createSeriesMarkers,
    HistogramSeries,
    IChartApi,
    IRange,
    ISeriesApi,
    ISeriesMarkersPluginApi,
    LineSeries,
    SeriesMarker,
    Time,
    UTCTimestamp,
    WhitespaceData
} from 'lightweight-charts';
import {ForecastBatchCandle, ForecastBatchSeries, ForecastSlotData,} from './ForecastBatchSeries';

// 时间戳格式检测阈值：10000000000 对应 2286-11-20，大于此值为毫秒级
const TIMESTAMP_MS_THRESHOLD = 10000000000;

/**
 * 各指标类型的多周期颜色数组（按周期升序排列时依次分配）
 * 颜色之间需有足够区分度，且不与其他指标类型的颜色冲突
 */
export const MULTI_PERIOD_COLORS: Record<string, string[]> = {
    // EMA：蓝色系 → 深蓝、天蓝
    EMA: ['#4d9fff', '#00b4d8', '#0077b6', '#48cae4'],
    // SMA：玫红/橙色系
    SMA: ['#eb2f96', '#fa541c', '#fadb14', '#a8071a'],
    // RSI：紫色系 → 深紫、中紫
    RSI: ['#9254de', '#d46b08', '#389e0d', '#0958d9'],
    // WMA：绿色系
    WMA: ['#00d97e', '#73d13d', '#95de64', '#b7eb8f'],
    // CCI：青色系
    CCI: ['#13c2c2', '#36cfc9', '#5cdbd3', '#87e8de'],
    // ATR：橙色系
    ATR: ['#f5a623', '#ffa940', '#ffc06d', '#ffd591'],
    // OBV：蓝色系（与EMA区分）
    OBV: ['#2f54eb', '#597ef7', '#85a5ff', '#adc6ff'],
    // ADX：红色系
    ADX: ['#f5222d', '#ff5252', '#ff7875', '#ffa39e'],
};

/**
 * KDJ 多周期颜色组：每个周期有独立色系，K/D/J 三线在同一色系内有明暗区分
 */
const KDJ_COLOR_SETS = [
    // period 0：橙色系（K实线、D亮橙、J深橙）
    {K: '#f5a623', D: '#ffc53d', J: '#ad4e00'},
    // period 1：青色系（K实线、D亮青、J深青）
    {K: '#13c2c2', D: '#5cdbd3', J: '#006d75'},
    // period 2：洋红色系（K实线、D粉红、J深红）
    {K: '#eb2f96', D: '#ff85c2', J: '#9e1068'},
] as const;

/**
 * 将时间戳转换为秒级 UTCTimestamp
 * 自动检测输入是秒级（10位）还是毫秒级（13位）
 */
const toUTCTimestamp = (time: number): UTCTimestamp => {
    if (time >= TIMESTAMP_MS_THRESHOLD) {
        // 毫秒级，需要除以 1000
        return Math.floor(time / 1000) as UTCTimestamp;
    }
    // 秒级，直接使用
    return Math.floor(time) as UTCTimestamp;
};

const toLineSeriesData = (
    values: TechnicalIndicatorData[],
    field: 'value' | 'upper' | 'middle' | 'lower' | 'signal' | 'diff' | 'k' | 'd' | 'j',
): Array<{ time: UTCTimestamp; value?: number }> => {
    return values.flatMap(v => {
        const time = toUTCTimestamp(v.time);
        const value = v[field];
        return value == null || !Number.isFinite(Number(value))
            ? []
            : [{time, value: Number(value)}];
    });
};

const toHistogramSeriesData = (
    values: TechnicalIndicatorData[],
): Array<{ time: UTCTimestamp; value?: number; color?: string }> => {
    return values.flatMap(v => {
        const time = toUTCTimestamp(v.time);
        if (v.histogram == null || !Number.isFinite(Number(v.histogram))) {
            return [];
        }
        const histogram = Number(v.histogram);
        return {
            time,
            value: histogram,
            color: histogram >= 0 ? 'rgba(0, 217, 126, 0.5)' : 'rgba(255, 82, 82, 0.5)',
        };
    });
};

/** 获取时间帧对应的毫秒偏移量 */
const getTimeframeMs = (tf: string): number => {
    const map: Record<string, number> = {
        '1m': 60000,
        '5m': 300000,
        '1h': 3600000,
        '4h': 14400000,
        '1d': 86400000,
    };
    return map[tf] || 3600000;
};

export type KLineBar = {
    time: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    confirmed?: boolean;
};

export interface TechnicalIndicatorData {
    time: number;
    value?: number;
    upper?: number;
    middle?: number;
    lower?: number;
    signal?: number;
    histogram?: number;
    diff?: number;
    k?: number;
    d?: number;
    j?: number;
}

/** 条件命中高亮段：指定 pane 内逐根夹层（top/bottom 为指标值坐标） */
export interface HighlightSegment {
    pane: 'main' | 'rsi' | 'macd';
    color: string;
    points: Array<{ time: number; top: number; bottom: number }>;
}

interface LightweightCandlestickChartProps {
    data: KLineBar[];
    /** 可选的第二组K线，用透明实体叠加展示，适合预测/实际走势对照。 */
    comparisonData?: KLineBar[];
    /** 多个相互独立的预测批次；每批保留独立 series、颜色和显隐状态。 */
    comparisonBatches?: Array<{
        id: number | string;
        color: string;
        data: KLineBar[];
        visible?: boolean;
        emphasized?: boolean;
    }>;
    /** 预测批次 id → 悬浮窗标签（如 "V2 #375"）；缺省回退显示 "#id" */
    comparisonBatchLabels?: Record<string, string>;
    eventMarkers?: Array<{
        time: number;
        position: 'aboveBar' | 'belowBar' | 'inBar';
        color: string;
        shape: 'circle' | 'square' | 'arrowUp' | 'arrowDown';
        text?: string;
    }>;
    focusTimeRange?: { from: number; to: number } | null;
    width?: number;
    height?: number;
    markPrice?: number | null;
    markPriceColor?: string;
    maxVisibleBars?: number;
    timeFrame?: string;
    loading?: boolean;
    indicators?: {
        EMA?: TechnicalIndicatorData[];
        SMA?: TechnicalIndicatorData[];
        WMA?: TechnicalIndicatorData[];
        RSI?: TechnicalIndicatorData[];
        MACD?: TechnicalIndicatorData[];
        BOLL?: TechnicalIndicatorData[];
        KDJ?: TechnicalIndicatorData[];
        CCI?: TechnicalIndicatorData[];
        ATR?: TechnicalIndicatorData[];
        OBV?: TechnicalIndicatorData[];
        ADX?: TechnicalIndicatorData[];
    };
    visibleIndicators?: Record<string, boolean>;
    /** 指标颜色映射：{ 'EMA_7': '#4d9fff', ... }，优先于默认配色 */
    indicatorColorMap?: Record<string, string>;
    /** 指标周期标签映射：{ 'BOLL_20': '20,2' }，用于浮窗展示完整的周期参数 */
    indicatorPeriodLabels?: Record<string, string>;
    referenceLines?: {
        avgPx?: number;
        closePx?: number;
        takeProfitPx?: number;
        targetTakeProfitPx?: number;
        stopLossPx?: number;
        liquidationPx?: number;
        forecastConnector?: {
            startTime: number;
            startPrice: number;
            endTime: number;
            endPrice: number;
        };
        cTime?: number;       // 开仓时间（毫秒时间戳）
        uTime?: number;       // 平仓时间（毫秒时间戳）
        posSide?: string; // 持仓方向：long=做多，short=做空
        // K线形态可视化支撑/阻力线（支持时间范围）
        supportLines?: Array<{ price: number; startTime?: number; endTime?: number; label?: string }>;    // 支撑线（蓝色）
        resistanceLines?: Array<{ price: number; startTime?: number; endTime?: number; label?: string }>; // 阻力线（橙色）
    };
    onTooltip?: (payload: {
        time: number;
        open: number;
        high: number;
        low: number;
        close: number;
        volume: number
    }) => void;
    onComparisonTooltip?: (payload: {
        time: number;
        batches: Array<{
            id: string;
            open: number;
            high: number;
            low: number;
            close: number;
        }>;
    } | null) => void;
    // 调试用：强制清除所有价格线
    debugForceClearPriceLines?: boolean;
    // 是否显示开仓价水平线（历史持仓时设为false，只显示标签）
    showEntryLine?: boolean;
    // 是否锁定右边缘，禁止向右滑动（历史持仓时设为true）
    lockRightEdge?: boolean;
    // 是否显示开仓价标签（Y轴标签）
    showAvgPx?: boolean;
    // 是否显示平仓价标签（Y轴标签）
    showClosePx?: boolean;
    // 初始可视范围居中区间（时间戳毫秒），覆盖默认的定位到最新K线行为
    centerTimeRange?: { from: number; to: number };
    // 禁用水平缩放和滚动，并在图表区域显示全部数据
    disableHorizontalZoom?: boolean;
    // 数据轮询更新时保留用户当前缩放/平移范围；停留在最右侧时自动跟随新K线。
    preserveViewportOnDataUpdate?: boolean;
    // 自定义时间格式化映射（时间戳秒 -> 格式化字符串）
    timeFormatterMap?: Record<number, string>;
    // 条件命中高亮段（可选；用于 DCA 等条件可视化，缺省不绘制）
    highlightSegments?: HighlightSegment[];
}

const LightweightCandlestickChart: React.FC<LightweightCandlestickChartProps> = ({
                                                                                     data,
                                                                                     comparisonData,
                                                                                     comparisonBatches,
                                                                                     eventMarkers,
                                                                                     focusTimeRange,
                                                                                     width,
                                                                                     height,
                                                                                     markPrice,
                                                                                     markPriceColor,
                                                                                     maxVisibleBars,
                                                                                     timeFrame,
                                                                                     indicators,
                                                                                     visibleIndicators = {},
                                                                                     indicatorColorMap,
                                                                                     indicatorPeriodLabels,
                                                                                     referenceLines,
                                                                                     loading = false,
                                                                                     onTooltip,
                                                                                     onComparisonTooltip,
                                                                                     debugForceClearPriceLines = false,
                                                                                     showEntryLine = true,
                                                                                     lockRightEdge = true,
                                                                                     showAvgPx = true,
                                                                                     showClosePx = true,
                                                                                     centerTimeRange,
                                                                                     disableHorizontalZoom = false,
                                                                                     preserveViewportOnDataUpdate = false,
                                                                                     timeFormatterMap,
                                                                                     comparisonBatchLabels,
                                                                                     highlightSegments,
                                                                                 }) => {
    const hasForecastConnector = Boolean(referenceLines?.forecastConnector);
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
    const comparisonSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
    const forecastBatchSeriesRef = useRef<ISeriesApi<
        'Custom',
        Time,
        ForecastSlotData | WhitespaceData<Time>
    > | null>(null);
    const forecastBatchLookupRef = useRef<Record<number, ForecastBatchCandle[]>>({});
    const comparisonTooltipRef = useRef(onComparisonTooltip);
    comparisonTooltipRef.current = onComparisonTooltip;
    const eventMarkersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
    const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
    const indicatorSeriesRef = useRef<Record<string, any>>({});
    const [chartReady, setChartReady] = useState(false);
    const [containerSize, setContainerSize] = useState({width: 0, height: 0});

    const priceLineRefs = useRef<any[]>([]);
    const markPriceLineRef = useRef<any>(null);
    /** Y 轴是否处于"可视范围锁定"状态（存在参考线时为 true，visibleTimeRangeChange 时驱动重算）*/
    const hasVisibleRangeLockRef = useRef(false);
    /** 实时价格越界补偿虚线 Series */
    const markPriceExtensionSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
    const entryLineSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
    const supportResistanceLineRefs = useRef<ISeriesApi<'Line'>[]>([]);  // 支撑阻力线 Series 引用
    const supportResistancePriceLineRefs = useRef<any[]>([]);  // 支撑阻力线 PriceLine 标签引用

    // 指标数据查找表：时间戳(秒) → { indicatorKey → 数据点 }
    const indicatorDataLookupRef = useRef<Record<number, Record<string, any>>>({});
    // 指标颜色索引：indicatorKey → 同类型中的排序索引
    const indicatorColorIndexRef = useRef<Record<string, number>>({});
    // Pane 索引映射：indicatorType → paneIndex（持久化分配）
    const paneIndexMapRef = useRef<Record<string, number>>({});
    // 参考线已添加标记（用于 RSI 超买超卖线等，每个类型只添加一次）
    const refLinesAddedRef = useRef<Set<string>>(new Set());

    // Tooltip 状态（open 等为 undefined 时表示纯预测槽位，batches 为命中该时刻的预测批次）
    const [tooltipData, setTooltipData] = useState<{
        time: number;
        open?: number;
        high?: number;
        low?: number;
        close?: number;
        volume?: number;
        batches?: ForecastBatchCandle[];
    } | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
    // 当前十字线所在时刻的指标值
    const [tooltipIndicators, setTooltipIndicators] = useState<Record<string, any> | null>(null);
    // 当前十字线是否在右侧最后30根K线区域内（tooltip向左展开）
    const [isRightSide, setIsRightSide] = useState(false);
    const dataLengthRef = useRef(data?.length || 0);
    // 保持 dataLengthRef 同步
    dataLengthRef.current = data?.length || 0;
    // 用于在 initChart 内部获取最新的 data 值（initChart useCallback 不含 data 依赖）
    const dataRef = useRef(data);
    dataRef.current = data;
    const comparisonDataRef = useRef(comparisonData);
    comparisonDataRef.current = comparisonData;
    const renderedCandleCountRef = useRef(0);
    const appliedFocusRangeRef = useRef<string | null>(null);

    const safeSetData = useCallback((series: any, label: string, data: any) => {
        if (!series) return;
        try {
            series.setData(data);
        } catch (e) {
            console.warn(`[LightweightCandlestickChart] ${label} setData failed:`, e);
        }
    }, []);

    // 优先使用传入的 height prop，否则使用容器实际高度（从 ResizeObserver 获取），最后默认 500
    const chartHeight = height || containerSize.height || 500;

    // 防抖标志：避免 initChart 被快速连续调用
    const initChartInProgressRef = useRef(false);
    // 图表更新标志：防止 removeSeries/addSeries 操作期间触发 subscribeCrosshairMove 回调导致无限循环
    const isUpdatingRef = useRef(false);
    const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
    const highlightCanvasRef = useRef<HTMLCanvasElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    /**
     * 绘制止盈止损区域背景（只在 K 线主图区域，不包含下方成交量副图和技术指标副图）
     * 做多时：止盈区域（顶部到止盈价）绿色，止损区域（止损价到底部）红色
     * 做空时：止损区域（顶部到止损价）红色，止盈区域（止盈价到底部）绿色
     * 同时绘制开仓/平仓围闭区域
     */
    const drawTPSLBackground = useCallback(() => {
        const canvas = overlayCanvasRef.current;
        const chart = chartRef.current;
        const series = candlestickSeriesRef.current;

        if (!canvas || !chart || !series || !referenceLines) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // 将价格转换为数字类型
        const takeProfitPxRaw = referenceLines.targetTakeProfitPx ?? referenceLines.takeProfitPx;
        const stopLossPxRaw = referenceLines.stopLossPx;
        const posSide = referenceLines.posSide || 'long'; // 默认为做多

        const takeProfitPx = typeof takeProfitPxRaw === 'string' ? parseFloat(takeProfitPxRaw) : (takeProfitPxRaw ?? undefined);
        const stopLossPx = typeof stopLossPxRaw === 'string' ? parseFloat(stopLossPxRaw) : (stopLossPxRaw ?? undefined);

        // 获取开仓/平仓价格和时间
        const avgPx = referenceLines.avgPx;
        const closePx = referenceLines.closePx;
        const cTime = referenceLines.cTime;
        const uTime = referenceLines.uTime;

        const connector = referenceLines.forecastConnector;

        // 没有止盈止损配置、没有开仓/平仓配置且没有预测连线时清空画布
        if (takeProfitPx === undefined && stopLossPx === undefined &&
            (avgPx === undefined || closePx === undefined || cTime === undefined || uTime === undefined) &&
            !connector) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            return;
        }

        // 获取 DPR 缩放因子（必须先获取，因为在 priceToY 中使用）
        const dpr = window.devicePixelRatio || 1;

        // 获取价格坐标转换函数
        // 注意：priceToCoordinate 返回逻辑像素，需要乘以 DPR 才能在 canvas 上正确绘制
        const priceToY = (price: number): number | null => {
            const coord = series.priceToCoordinate(price);
            if (coord === null) return null;
            return coord * dpr;  // 转换为 DPR 缩放后的实际像素
        };

        // 在K线数据中找到最接近目标时间的bar时间（用于时间帧对齐）
        const findClosestBarTime = (targetTime: number): number | null => {
            if (!data || data.length === 0) return null;
            // targetTime 是毫秒时间戳，data[i].time 也是毫秒时间戳
            let closest = data[0].time;
            let minDiff = Math.abs(data[0].time - targetTime);
            for (let i = 1; i < data.length; i++) {
                const diff = Math.abs(data[i].time - targetTime);
                if (diff < minDiff) {
                    minDiff = diff;
                    closest = data[i].time;
                }
            }
            return closest;
        };

        // 获取时间坐标转换函数。
        // 对已存在的 bar 优先直接命中；对未来预期时间则回退到 logical index，
        // 这样即使终点尚未出现在实际K线数据里，也能在扩展后的可视范围中计算出X坐标。
        const timeToX = (timeMs: number): number | null => {
            const timeScale = chart.timeScale();
            const timeSec = Math.floor(timeMs / 1000);
            const exactCoord = timeScale.timeToCoordinate(timeSec as any);
            if (exactCoord !== null) {
                return exactCoord * dpr;
            }
            if (!data || data.length === 0) {
                return null;
            }

            const intervalMs = getTimeframeMs(timeFrame || '5m');
            const firstBarTime = data[0].time;
            const lastBarTime = data[data.length - 1].time;

            if (timeMs > lastBarTime) {
                const extraBars = Math.max(1, Math.ceil((timeMs - lastBarTime) / intervalMs));
                const futureLogical = (data.length - 1) + extraBars;
                const futureCoord = timeScale.logicalToCoordinate(futureLogical as Logical);
                return futureCoord === null ? null : futureCoord * dpr;
            }

            if (timeMs < firstBarTime) {
                const prevBars = Math.max(1, Math.ceil((firstBarTime - timeMs) / intervalMs));
                const prevLogical = 0 - prevBars;
                const prevCoord = timeScale.logicalToCoordinate(prevLogical as Logical);
                return prevCoord === null ? null : prevCoord * dpr;
            }

            const alignedBarTime = findClosestBarTime(timeMs);
            if (alignedBarTime === null) {
                return null;
            }
            const alignedCoord = timeScale.timeToCoordinate(Math.floor(alignedBarTime / 1000) as any);
            return alignedCoord === null ? null : alignedCoord * dpr;
        };

        // 使用 canvas 的实际像素尺寸（已乘以 DPR）
        const canvasWidth = canvas.width;  // 已经是 DPR 缩放后的值
        const canvasHeight = canvas.height;  // 已经是 DPR 缩放后的值

        // 获取主图 pane 的实际高度（因为可能有多个副图，每个 pane 高度不同）
        // chart.panes()[0] 是主图（K线 pane），使用 getHeight() 获取其实际像素高度
        const panes = chart.panes();
        const mainPaneHeight = panes.length > 0 ? panes[0].getHeight() * dpr : canvasHeight;

        // 清空画布
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        const isLong = posSide === 'long';

        // 绘制止盈止损区域
        if (isLong) {
            // 做多：止盈在上，止损在下
            if (takeProfitPx !== undefined) {
                const tpY = priceToY(takeProfitPx);
                if (tpY !== null && tpY > 0) {
                    ctx.fillStyle = 'rgba(82, 196, 26, 0.15)';
                    ctx.fillRect(0, 0, canvasWidth, tpY);
                }
            }
            if (stopLossPx !== undefined) {
                const slY = priceToY(stopLossPx);
                if (slY !== null) {
                    ctx.fillStyle = 'rgba(255, 77, 79, 0.15)';
                    ctx.fillRect(0, slY, canvasWidth, mainPaneHeight - slY);
                }
            }
        } else {
            // 做空：止损在上，止盈在下
            if (stopLossPx !== undefined) {
                const slY = priceToY(stopLossPx);
                if (slY !== null && slY > 0) {
                    ctx.fillStyle = 'rgba(255, 77, 79, 0.15)';
                    ctx.fillRect(0, 0, canvasWidth, slY);
                }
            }
            if (takeProfitPx !== undefined) {
                const tpY = priceToY(takeProfitPx);
                if (tpY !== null) {
                    ctx.fillStyle = 'rgba(82, 196, 26, 0.15)';
                    ctx.fillRect(0, tpY, canvasWidth, mainPaneHeight - tpY);
                }
            }
        }

        // 绘制开仓/平仓围闭区域（历史仓位）
        if (avgPx !== undefined && closePx !== undefined && cTime !== undefined && uTime !== undefined) {
            const openY = priceToY(avgPx);
            const closeY = priceToY(closePx);

            // 在K线数据中找到最接近开仓/平仓时间的bar时间
            const openBarTime = findClosestBarTime(cTime);
            const closeBarTime = findClosestBarTime(uTime);

            // 将bar时间转换为X坐标
            const openX = openBarTime !== null ? timeToX(openBarTime) : null;
            const closeX = closeBarTime !== null ? timeToX(closeBarTime) : null;

            if (openY !== null && closeY !== null && openX !== null && closeX !== null) {
                // 计算矩形位置和尺寸
                const rectX = Math.min(openX, closeX);
                const rectY = Math.min(openY, closeY);
                const rectWidth = Math.abs(closeX - openX);
                const rectHeight = Math.abs(closeY - openY);

                // 判断盈利或亏损
                const isProfit = isLong ? closePx > avgPx : closePx < avgPx;

                // 设置颜色（盈利绿色，亏损红色）
                ctx.fillStyle = isProfit ? 'rgba(82, 196, 26, 0.15)' : 'rgba(255, 77, 79, 0.15)';

                // 绘制矩形区域
                ctx.fillRect(rectX, rectY, rectWidth, rectHeight);

                // 绘制边框
                ctx.strokeStyle = isProfit ? 'rgba(82, 196, 26, 0.5)' : 'rgba(255, 77, 79, 0.5)';
                ctx.lineWidth = 1 * dpr;
                ctx.strokeRect(rectX, rectY, rectWidth, rectHeight);
            }
        }

        if (connector) {
            const startX = timeToX(connector.startTime);
            const startY = priceToY(connector.startPrice);
            const endX = timeToX(connector.endTime);
            const endY = priceToY(connector.endPrice);

            if (startX !== null && startY !== null && endX !== null && endY !== null) {
                ctx.save();
                ctx.strokeStyle = 'rgba(19, 194, 194, 0.95)';
                ctx.lineWidth = Math.max(1, dpr);
                ctx.setLineDash([6 * dpr, 4 * dpr]);
                ctx.beginPath();
                ctx.moveTo(startX, startY);
                ctx.lineTo(endX, endY);
                ctx.stroke();
                ctx.setLineDash([]);

                ctx.fillStyle = 'rgba(19, 194, 194, 0.95)';
                ctx.beginPath();
                ctx.arc(startX, startY, 2.5 * dpr, 0, Math.PI * 2);
                ctx.fill();
                ctx.beginPath();
                ctx.arc(endX, endY, 3 * dpr, 0, Math.PI * 2);
                ctx.fill();

                ctx.font = `${12 * dpr}px sans-serif`;
                ctx.textAlign = endX >= startX ? 'left' : 'right';
                ctx.textBaseline = 'bottom';
                const labelX = endX >= startX ? endX + 6 * dpr : endX - 6 * dpr;
                const labelY = endY - 6 * dpr;
                ctx.fillText('持仓预期', labelX, labelY);
                ctx.restore();
            }
        }

    }, [referenceLines, data, timeFrame]);

    /**
     * 绘制条件命中高亮段（独立 overlay canvas，避免与止盈止损背景相互影响）。
     * 逐根以 top/bottom（指标值坐标）转像素绘制半透明夹层；缺省无高亮时清空画布。
     */
    const drawHighlights = useCallback(() => {
        const canvas = highlightCanvasRef.current;
        const chart = chartRef.current;
        if (!canvas) {
            return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }
        const dpr = window.devicePixelRatio || 1;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (!chart || !highlightSegments || highlightSegments.length === 0) {
            return;
        }

        // pane → 代表性 series（用于价格→像素坐标换算；priceToCoordinate 返回整图坐标）
        const resolveSeries = (pane: HighlightSegment['pane']) => {
            if ('main' === pane) {
                return candlestickSeriesRef.current;
            }
            const prefix = 'rsi' === pane ? 'RSI' : 'MACD';
            const keys = Object.keys(indicatorSeriesRef.current);
            const key = keys.find((k) => {
                const base = k.split('_')[0];
                if (base !== prefix) {
                    return false;
                }
                return 'MACD' !== prefix || k.endsWith('_DIF');
            });
            return key ? indicatorSeriesRef.current[key] : null;
        };

        const timeScale = chart.timeScale();
        const barSpacing = timeScale.options().barSpacing ?? 8;

        highlightSegments.forEach((seg) => {
            const series = resolveSeries(seg.pane);
            if (!series) {
                return;
            }
            seg.points.forEach((p) => {
                const x = timeScale.timeToCoordinate(Math.floor(p.time / 1000) as any);
                const yTop = series.priceToCoordinate(p.top);
                const yBottom = series.priceToCoordinate(p.bottom);
                if (x === null || yTop === null || yBottom === null) {
                    return;
                }
                // 两条指标线可能非常接近：给夹层一个最小可视高度，避免细到看不见
                const MIN_BAND = 3;
                let yMin = Math.min(yTop, yBottom);
                let yMax = Math.max(yTop, yBottom);
                if (yMax - yMin < MIN_BAND) {
                    const mid = (yMin + yMax) / 2;
                    yMin = mid - MIN_BAND / 2;
                    yMax = mid + MIN_BAND / 2;
                }
                ctx.fillStyle = seg.color;
                ctx.fillRect(
                    x * dpr - (barSpacing / 2) * dpr,
                    yMin * dpr,
                    barSpacing * dpr,
                    (yMax - yMin) * dpr,
                );
            });
        });
    }, [highlightSegments]);

    /**
     * 将 Y 轴价格刻度锁定为"当前可视窗口"的范围（可视 K 线 + 可视主图指标 + 参考线价格），
     * 由价格线创建 effect 初始化、visibleTimeRangeChange 订阅在拖动/缩放时驱动重算，
     * 修复水平滑动后 Y 轴不跟随可视范围导致 K 线扁平的问题（2026-08-01）
     */
    const applyPriceScaleToVisibleRange = useCallback((range: IRange<Time> | null) => {
        if (!chartReady || !chartRef.current || data.length === 0) return;
        const fromTime = range ? toUTCTimestamp(Number(range.from)) : null;
        const toTime = range ? toUTCTimestamp(Number(range.to)) : null;

        const allPrices: number[] = [];

        // 1. 可视窗口内 K 线高低（data 升序，超出 to 提前终止）
        for (const bar of data) {
            const barTime = toUTCTimestamp(bar.time);
            if (fromTime !== null && barTime < fromTime) continue;
            if (toTime !== null && barTime > toTime) break;
            allPrices.push(bar.high, bar.low);
        }

        // 2. 可视窗口内主图指标的价格（BOLL 的 upper/lower、EMA 等），排除副图振荡指标
        if (indicators) {
            Object.entries(indicators).forEach(([key, values]) => {
                if (!values || !Array.isArray(values)) return;
                const baseType = key.split('_')[0];
                const periodWithPrefix = key.substring(baseType.length + 1);
                const period = periodWithPrefix.replace(/^(ema|rsi|boll|macd|kdj)_/i, '').replace(/,/g, '_');
                const visibleKey = `${baseType}_${period}`;
                const isVisible = visibleIndicators[visibleKey] !== undefined
                    ? visibleIndicators[visibleKey]
                    : Object.keys(visibleIndicators).some(k => k.startsWith(baseType) && visibleIndicators[k]);
                if (!isVisible) return;
                const isOscillator = ['MACD', 'KDJ', 'RSI', 'CCI', 'OBV', 'ADX'].includes(baseType);
                if (isOscillator) return;

                values.forEach((v: any) => {
                    const pointTime = toUTCTimestamp(Number(v.time));
                    if (fromTime !== null && pointTime < fromTime) return;
                    if (toTime !== null && pointTime > toTime) return;
                    if (v.upper !== undefined) allPrices.push(v.upper);
                    if (v.middle !== undefined) allPrices.push(v.middle);
                    if (v.lower !== undefined) allPrices.push(v.lower);
                    if (v.value !== undefined) allPrices.push(v.value);
                });
            });
        }

        // 3. 可视窗口内预测K线（comparisonData）的高低，防止预测弹窗中的预测K线被截断
        for (const bar of (comparisonData || [])) {
            const barTime = toUTCTimestamp(bar.time);
            if (fromTime !== null && barTime < fromTime) continue;
            if (toTime !== null && barTime > toTime) break;
            allPrices.push(bar.high, bar.low);
        }

        // 4. 可视窗口内预测K线批次（comparisonBatches）的高低
        for (const batch of (comparisonBatches || [])) {
            if (batch.visible === false) continue;
            for (const bar of batch.data) {
                const barTime = toUTCTimestamp(bar.time);
                if (fromTime !== null && barTime < fromTime) continue;
                if (toTime !== null && barTime > toTime) break;
                allPrices.push(bar.high, bar.low);
            }
        }

        // 5. 参考线价格（与价格线创建逻辑的收集规则保持一致）：
        //    普通参考线（TP/SL/强平价等）+ 开仓/平仓价标签 + 标记价，全局锚点不受可视窗口过滤
        const priceSeen = new Set<number>();
        const collectPrice = (price: number) => {
            if (Number.isFinite(price) && price > 0 && !priceSeen.has(price)) {
                priceSeen.add(price);
                allPrices.push(price);
            }
        };
        Object.entries(referenceLines || {}).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            // 支撑/阻力线是数组、avgPx/cTime/uTime 有专用处理，按现有规则跳过
            if (key === 'supportLines' || key === 'resistanceLines') return;
            if (key === 'avgPx' || key === 'cTime' || key === 'uTime' || key === 'forecastConnector') return;
            if (key === 'closePx' && !showEntryLine) return;
            collectPrice(typeof value === 'string' ? parseFloat(value) : Number(value));
        });
        if (referenceLines?.forecastConnector) {
            collectPrice(Number(referenceLines.forecastConnector.startPrice));
            collectPrice(Number(referenceLines.forecastConnector.endPrice));
        }
        if (showAvgPx && referenceLines?.avgPx !== undefined) collectPrice(Number(referenceLines.avgPx));
        if (!showEntryLine && showClosePx && referenceLines?.closePx !== undefined) {
            collectPrice(Number(referenceLines.closePx));
        }
        if (markPrice !== undefined && markPrice !== null) {
            collectPrice(typeof markPrice === 'string' ? parseFloat(markPrice) : Number(markPrice));
        }

        if (allPrices.length === 0) return;

        const minPrice = Math.min(...allPrices);
        const maxPrice = Math.max(...allPrices);
        const margin = (maxPrice - minPrice) * 0.05;

        const priceScale = chartRef.current.priceScale('right');
        priceScale.setAutoScale(false);
        priceScale.setVisibleRange({from: minPrice - margin, to: maxPrice + margin});
    }, [chartReady, data, comparisonData, comparisonBatches, indicators, visibleIndicators, referenceLines, markPrice, showEntryLine, showAvgPx, showClosePx]);

    // 监听图表变化并重绘止盈止损背景
    useEffect(() => {
        if (!chartReady || !referenceLines) return;

        // 增加延迟确保 lightweight-charts 完成 panes 更新后再获取高度
        const timer = setTimeout(() => {
            drawTPSLBackground();
        }, 50);

        return () => clearTimeout(timer);
    }, [chartReady, referenceLines, indicators, visibleIndicators, drawTPSLBackground]);

    // 监听条件命中高亮段变化并重绘（独立 canvas；延迟确保副图 pane 就绪）
    useEffect(() => {
        if (!chartReady) return;
        const timer = setTimeout(() => {
            drawHighlights();
        }, 50);
        return () => clearTimeout(timer);
    }, [chartReady, highlightSegments, indicators, visibleIndicators, drawHighlights,
        containerSize.width, containerSize.height]);

    // 监听图表可见范围变化（滑动K线时重绘开平仓区域 + 锁定态下重算 Y 轴跟随可视范围 + 重绘高亮）
    useEffect(() => {
        if (!chartReady || !chartRef.current) return;

        const timeScale = chartRef.current.timeScale();
        const handler = (range: IRange<Time> | null) => {
            drawTPSLBackground();
            drawHighlights();
            if (hasVisibleRangeLockRef.current) applyPriceScaleToVisibleRange(range);
        };

        timeScale.subscribeVisibleTimeRangeChange(handler);

        return () => {
            timeScale.unsubscribeVisibleTimeRangeChange(handler);
        };
    }, [chartReady, drawTPSLBackground, drawHighlights, applyPriceScaleToVisibleRange]);

    useEffect(() => {
        if (!containerRef.current) return;
        const el = containerRef.current;
        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) {
                const {width, height} = entry.contentRect;
                if (width > 0 && height > 0) {
                    setContainerSize({width: Math.floor(width), height: Math.floor(height)});
                }
            }
        });
        ro.observe(el);
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
            setContainerSize({width: Math.floor(rect.width), height: Math.floor(rect.height)});
        }
        return () => ro.disconnect();
    }, []);

    // 当 indicators 变化时，重建查找表和颜色索引表
    useEffect(() => {
        // 重建颜色索引：同类型按周期升序排列，分配索引
        const colorIndexByKey: Record<string, number> = {};
        if (indicators) {
            const keysByBaseType: Record<string, string[]> = {};
            Object.keys(indicators).forEach(k => {
                const bt = k.split('_')[0];
                if (!keysByBaseType[bt]) keysByBaseType[bt] = [];
                keysByBaseType[bt].push(k);
            });
            Object.values(keysByBaseType).forEach(keys => {
                keys.sort((a, b) => {
                    const pa = parseInt(a.replace(/^[A-Z]+_[a-z]+_/i, ''));
                    const pb = parseInt(b.replace(/^[A-Z]+_[a-z]+_/i, ''));
                    return (isNaN(pa) ? 0 : pa) - (isNaN(pb) ? 0 : pb);
                }).forEach((k, idx) => {
                    colorIndexByKey[k] = idx;
                });
            });
        }
        indicatorColorIndexRef.current = colorIndexByKey;

        // 重建时间查找表：时间戳(秒) → { key → 数据点 }
        const lookup: Record<number, Record<string, any>> = {};
        if (indicators) {
            Object.entries(indicators).forEach(([key, values]) => {
                if (!values || !Array.isArray(values)) return;
                (values as any[]).forEach(v => {
                    const ts = toUTCTimestamp(v.time);
                    if (!lookup[ts]) lookup[ts] = {};
                    lookup[ts][key] = v;
                });
            });
        }
        indicatorDataLookupRef.current = lookup;
    }, [indicators]);

    const initChart = useCallback(() => {
        // 防抖：如果已经在初始化中，跳过
        if (initChartInProgressRef.current) return;

        if (!containerRef.current) return;

        const container = containerRef.current;
        // 直接从 DOM 获取实际宽高，不再依赖 containerSize state
        const chartWidth = width || container.clientWidth;
        const initHeight = height || container.clientHeight || 500;
        if (chartWidth === 0) return;

        // 设置防抖标志
        initChartInProgressRef.current = true;

        // 关键修复：如果已有图表，先移除它（避免创建多个实例）
        if (chartRef.current) {
            try {
                chartRef.current.remove();
            } catch (e) {
                // console.debug('chart.remove error:', e);
            }
            chartRef.current = null;
            candlestickSeriesRef.current = null;
            comparisonSeriesRef.current = null;
            forecastBatchSeriesRef.current = null;
            forecastBatchLookupRef.current = {};
            eventMarkersRef.current = null;
            volumeSeriesRef.current = null;
            indicatorSeriesRef.current = {};
            paneIndexMapRef.current = {};  // 重置 pane 索引映射，避免残留导致空白副图
        }
        appliedFocusRangeRef.current = null;

        // 计算最小K线间距，限制可视范围内的K线数量上限
        // minBarSpacing = 图表宽度 / 最大可视K线数量
        let minBarSpacing = 0.5; // 默认值
        if (maxVisibleBars && maxVisibleBars > 0) {
            minBarSpacing = chartWidth / maxVisibleBars;
        }

        const formatChartTimeLabel = (timeValue: unknown): string | null => {
            if (typeof timeValue === 'number') {
                const normalizedTime = Math.floor(timeValue);
                if (timeFormatterMap && timeFormatterMap[normalizedTime]) {
                    return timeFormatterMap[normalizedTime];
                }
            }
            return null;
        };

        const chart = createChart(container, {
            width: chartWidth,
            height: initHeight,
            layout: {
                background: {type: ColorType.Solid, color: '#080b0f'},
                textColor: '#3d5469',
                attributionLogo: false,
            },
            grid: {
                vertLines: {color: '#1e2d3d', style: 3},
                horzLines: {color: '#1e2d3d', style: 3},
            },
            crosshair: {
                mode: 0,
            },
            timeScale: {
                timeVisible: true,
                secondsVisible: false,
                borderColor: '#1e2d3d',
                barSpacing: minBarSpacing,
                minBarSpacing: minBarSpacing,
                fixLeftEdge: disableHorizontalZoom ? true : undefined,
                fixRightEdge: disableHorizontalZoom ? true : (hasForecastConnector ? false : lockRightEdge),
                tickMarkFormatter: (time: unknown) => {
                    return formatChartTimeLabel(time);
                },
            },
            leftPriceScale: {
                visible: false,
            },
            rightPriceScale: {
                borderColor: '#1e2d3d',
            },
            localization: {
                timeFormatter: (time: number) => {
                    const mappedLabel = formatChartTimeLabel(time);
                    if (mappedLabel) {
                        return mappedLabel;
                    }
                    const date = new Date(time * 1000);
                    const year = String(date.getFullYear()).slice(-2);
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const hours = String(date.getHours()).padStart(2, '0');
                    const minutes = String(date.getMinutes()).padStart(2, '0');

                    // 根据时间帧格式化
                    if (timeFrame === '1m' || timeFrame === '5m') {
                        return `${year}-${month}-${day} ${hours}:${minutes}`;
                    } else if (timeFrame === '1h' || timeFrame === '4h') {
                        return `${year}-${month}-${day} ${hours}`;
                    } else {
                        return `${year}-${month}-${day}`;
                    }
                },
            },
            handleScroll: disableHorizontalZoom ? false : true,
            handleScale: disableHorizontalZoom ? false : true,
        });

        const candlestickSeries = chart.addSeries(CandlestickSeries, {
            upColor: '#00d97e',
            downColor: '#ff5252',
            borderUpColor: '#00d97e',
            borderDownColor: '#ff5252',
            wickUpColor: '#00d97e',
            wickDownColor: '#ff5252',
            priceLineVisible: false,
            lastValueVisible: false,
        });

        const comparisonSeries = chart.addSeries(CandlestickSeries, {
            upColor: 'rgba(22, 119, 255, 0.08)',
            downColor: 'rgba(250, 173, 20, 0.08)',
            borderUpColor: '#4d9fff',
            borderDownColor: '#f5a623',
            wickUpColor: '#4d9fff',
            wickDownColor: '#f5a623',
            priceLineVisible: false,
            lastValueVisible: false,
        });

        const volumeSeries = chart.addSeries(HistogramSeries, {
            priceFormat: {type: 'volume'},
            priceScaleId: '',
            priceLineVisible: false,
            lastValueVisible: false,
        });

        volumeSeries.priceScale().applyOptions({
            scaleMargins: {
                top: 0.8,   // K线占上方80%，成交量显示在下方20%
                bottom: 0,
            },
        });

        chartRef.current = chart;
        candlestickSeriesRef.current = candlestickSeries;
        eventMarkersRef.current = createSeriesMarkers(candlestickSeries, []);
        forecastBatchSeriesRef.current = chart.addCustomSeries(new ForecastBatchSeries(), {
            priceLineVisible: false,
            lastValueVisible: false,
        });
        comparisonSeriesRef.current = comparisonSeries;
        volumeSeriesRef.current = volumeSeries;

        // 创建 series 后立即同步设置已有数据，确保图表在首次渲染前有数据，
        // 避免因 initChart 重入导致 chartReady 值不变从而跳过数据 effect 执行，
        // 进而避免 lightweight-charts 在 render 循环中抛出 "Value is null"
        const initialData = dataRef.current;
        if (initialData && initialData.length > 0) {
            const sortedInitData = [...initialData].sort((a, b) => a.time - b.time);
            const sanitizedInitData = sortedInitData
                .filter(d => d != null && typeof d === 'object')
                .map(d => ({
                    time: d.time,
                    open: Number.isFinite(Number(d.open)) ? Number(d.open) : 0,
                    high: Number.isFinite(Number(d.high)) ? Number(d.high) : 0,
                    low: Number.isFinite(Number(d.low)) ? Number(d.low) : 0,
                    close: Number.isFinite(Number(d.close)) ? Number(d.close) : 0,
                    volume: d.volume,
                }))
                .filter(d => d.high >= d.low && d.high > 0);
            if (sanitizedInitData.length > 0) {
                const initCandleData = sanitizedInitData.map(d => ({
                    time: toUTCTimestamp(d.time),
                    open: d.open, high: d.high, low: d.low, close: d.close,
                }));
                try {
                    candlestickSeries.setData(initCandleData);
                    renderedCandleCountRef.current = initCandleData.length;
                } catch (e) {
                    console.warn('[LightweightCandlestickChart] init Candlestick setData failed:', e);
                }
                const initVolumeData = sanitizedInitData.map(d => {
                    const isUp = d.close >= d.open;
                    const isDoji = Math.abs(d.close - d.open) / (d.high - d.low + 0.0001) < 0.1;
                    let color = isUp ? 'rgba(0, 217, 126, 0.5)' : 'rgba(255, 82, 82, 0.5)';
                    if (isDoji) color = 'rgba(245, 166, 35, 0.5)';
                    return {time: toUTCTimestamp(d.time), value: d.volume || 0, color};
                });
                try {
                    volumeSeries.setData(initVolumeData);
                } catch (e) {
                    console.warn('[LightweightCandlestickChart] init Volume setData failed:', e);
                }
            }
        }

        const initialComparisonData = comparisonDataRef.current;
        if (initialComparisonData && initialComparisonData.length > 0) {
            const comparisonCandles = [...initialComparisonData]
                .sort((a, b) => a.time - b.time)
                .filter(d => d != null && Number(d.high) >= Number(d.low) && Number(d.high) > 0)
                .map(d => ({
                    time: toUTCTimestamp(d.time),
                    open: Number(d.open),
                    high: Number(d.high),
                    low: Number(d.low),
                    close: Number(d.close),
                }));
            safeSetData(comparisonSeries, 'Comparison Candlestick', comparisonCandles);
        }

        chart.subscribeCrosshairMove((param) => {
            // 防护：如果正在更新图表（removeSeries/addSeries 操作期间），跳过回调
            // 防止无限循环：removeSeries 触发回调 -> setState -> 重新渲染 -> useEffect -> removeSeries
            if (isUpdatingRef.current) {
                // 即使处于更新中，鼠标离开图表时也要清除 tooltip
                // 防止 isUpdatingRef 通过 requestAnimationFrame 延迟重置期间，退出事件被阻塞导致 tooltip 卡住
                if (!param.time || !param.point) {
                    setTooltipData(null);
                    setTooltipPosition(null);
                    setTooltipIndicators(null);
                    setIsRightSide(false);
                    comparisonTooltipRef.current?.(null);
                }
                return;
            }

            if (param.time && param.point) {
                const timeSeconds = param.time as number;
                const candleData = param.seriesData.get(candlestickSeries);
                const volumeData = param.seriesData.get(volumeSeries);
                if (candleData && 'open' in candleData) {
                    const barData = candleData as { open: number; high: number; low: number; close: number };
                    // 判断是否在右侧最后30根K线区域内
                    const logicalIndex = param.logical !== undefined ? Math.round(param.logical) : -1;
                    const totalBars = dataLengthRef.current;
                    const nearRightEdge = totalBars > 30 && logicalIndex >= 0 && logicalIndex >= totalBars - 30;
                    setIsRightSide(!!nearRightEdge);
                    setTooltipData({
                        time: timeSeconds * 1000,
                        open: barData.open,
                        high: barData.high,
                        low: barData.low,
                        close: barData.close,
                        volume: (volumeData && 'value' in volumeData ? volumeData.value : 0),
                        // 预测批次与实盘K线时间重叠时，悬浮窗同时展示预测数据
                        batches: forecastBatchLookupRef.current[timeSeconds] || [],
                    });
                    setTooltipPosition({x: param.point.x, y: param.point.y});
                    // 查出该时刻的指标数据
                    setTooltipIndicators(indicatorDataLookupRef.current[timeSeconds] || null);
                    if (onTooltip) {
                        onTooltip({
                            time: timeSeconds * 1000,
                            open: barData.open,
                            high: barData.high,
                            low: barData.low,
                            close: barData.close,
                            volume: 0
                        });
                    }
                } else {
                    // 纯预测槽位（无实盘K线）：有预测批次时悬浮窗仍展示（时间 + 批次行），否则隐藏
                    const batches = forecastBatchLookupRef.current[timeSeconds] || [];
                    if (batches.length > 0) {
                        setTooltipData({time: timeSeconds * 1000, batches});
                        setTooltipPosition({x: param.point.x, y: param.point.y});
                        setTooltipIndicators(indicatorDataLookupRef.current[timeSeconds] || null);
                    } else {
                        setTooltipData(null);
                        setTooltipPosition(null);
                        setTooltipIndicators(null);
                    }
                }
                const comparisonPayload = (forecastBatchLookupRef.current[timeSeconds] || [])
                    .map(value => ({
                        id: value.id,
                        open: value.open,
                        high: value.high,
                        low: value.low,
                        close: value.close,
                    }));
                comparisonTooltipRef.current?.({
                    time: timeSeconds * 1000,
                    batches: comparisonPayload,
                });
            } else {
                // 鼠标离开图表时清空 tooltip
                setTooltipData(null);
                setTooltipPosition(null);
                setTooltipIndicators(null);
                setIsRightSide(false);
                comparisonTooltipRef.current?.(null);
            }
        });

        const handleResize = () => {
            if (container && chartRef.current && chartReady) {
                const newWidth = container.clientWidth;
                const newHeight = container.clientHeight;
                if (newWidth > 0 && newHeight > 0) {
                    chartRef.current.applyOptions({
                        width: newWidth,
                        height: newHeight,
                    });
                }
            }
        };

        const resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(container);

        // 初始化完成后清除防抖标志
        initChartInProgressRef.current = false;
        setChartReady(true);

        return () => {
            resizeObserver.disconnect();
            chart.remove();
            chartRef.current = null;
            candlestickSeriesRef.current = null;
            comparisonSeriesRef.current = null;
            forecastBatchSeriesRef.current = null;
            forecastBatchLookupRef.current = {};
            eventMarkersRef.current = null;
            volumeSeriesRef.current = null;
            indicatorSeriesRef.current = {};
            appliedFocusRangeRef.current = null;
            // 重置 chartReady，确保下一次 initChart 执行时数据 effect 能正确响应
            setChartReady(false);
        };
    }, [timeFrame, disableHorizontalZoom, lockRightEdge, timeFormatterMap, hasForecastConnector]);

    useEffect(() => {
        initChart();
    }, [initChart]);

    // 当 containerSize 从无效变为有效时，确保图表被初始化
    useEffect(() => {
        if (containerSize.width > 0 && containerSize.height > 0 && !chartReady && !initChartInProgressRef.current) {
            initChart();
        }
    }, [containerSize, chartReady, initChart]);

    // 使用 useLayoutEffect 在 DOM 更新后立即调整图表大小
    useEffect(() => {
        if (!chartReady || !chartRef.current || !containerRef.current) return;
        if (containerSize.width === 0 || containerSize.height === 0) return;

        const newWidth = width || containerSize.width;
        const newHeight = height || containerSize.height;

        if (newWidth > 0 && newHeight > 0) {
            try {
                chartRef.current.applyOptions({
                    width: newWidth,
                    height: newHeight,
                });
            } catch (e) {
                console.error('[LightweightCandlestickChart] 调整图表大小失败:', e);
            }
        }
    }, [containerSize.width, containerSize.height, chartReady, width, height]);

    // maxVisibleBars 只限制最小柱间距（即最多可见多少根），不能写入实际 barSpacing。
    // 写入实际 barSpacing 会在 ResizeObserver 触发时重置用户缩放。
    useEffect(() => {
        if (!chartReady || !chartRef.current) return;

        const chartWidth = width || containerSize.width;
        if (chartWidth === 0) return;

        if (disableHorizontalZoom) {
            chartRef.current.timeScale().fitContent();
        } else if (maxVisibleBars && maxVisibleBars > 0) {
            chartRef.current.timeScale().applyOptions({
                minBarSpacing: chartWidth / maxVisibleBars,
            });
        }
    }, [maxVisibleBars, chartReady, width, containerSize, disableHorizontalZoom]);

    useEffect(() => {
        if (!chartReady || !candlestickSeriesRef.current || !volumeSeriesRef.current || data.length === 0) return;

        const sortedData = [...data].sort((a, b) => a.time - b.time);

        // 净化 OHLC 值：将 null/NaN/undefined/Infinity 转为 0，确保 lightweight-charts 不会抛出 "Value is null"
        // 相比 filter 丢弃，净化方式保留所有 bar 数量，保证滚动/视口计算的一致性
        const sanitizedData = sortedData
            .filter(d => d != null && typeof d === 'object')
            .map(d => ({
                time: d.time,
                open: Number.isFinite(Number(d.open)) ? Number(d.open) : 0,
                high: Number.isFinite(Number(d.high)) ? Number(d.high) : 0,
                low: Number.isFinite(Number(d.low)) ? Number(d.low) : 0,
                close: Number.isFinite(Number(d.close)) ? Number(d.close) : 0,
                volume: d.volume,
            }))
            .filter(d => d.high >= d.low && d.high > 0);

        const candleData = sanitizedData.map(d => ({
            time: toUTCTimestamp(d.time),
            open: d.open, high: d.high, low: d.low, close: d.close,
        }));

        const volumeData = sanitizedData.map(d => {
            const isUp = d.close >= d.open;
            const isDoji = Math.abs(d.close - d.open) / (d.high - d.low + 0.0001) < 0.1;
            let color = isUp ? 'rgba(0, 217, 126, 0.5)' : 'rgba(255, 82, 82, 0.5)';
            if (isDoji) color = 'rgba(245, 166, 35, 0.5)';
            return {time: toUTCTimestamp(d.time), value: d.volume || 0, color};
        });

        const previousRange = preserveViewportOnDataUpdate
            ? chartRef.current?.timeScale().getVisibleLogicalRange()
            : null;
        const previousTimeRange = preserveViewportOnDataUpdate
            ? chartRef.current?.timeScale().getVisibleRange()
            : null;
        const previousCount = renderedCandleCountRef.current;
        const wasAtRightEdge = previousRange != null
            && previousCount > 0
            && previousRange.to >= previousCount - 1.5;

        safeSetData(candlestickSeriesRef.current, 'Candlestick', candleData);
        safeSetData(volumeSeriesRef.current, 'Volume', volumeData);
        renderedCandleCountRef.current = candleData.length;

        if (disableHorizontalZoom && !(centerTimeRange && centerTimeRange.from && centerTimeRange.to)) {
            chartRef.current?.timeScale().fitContent();
            return;
        }

        // 居中模式：计算开平仓区间在 candleData 中的索引范围，居中显示
        if (centerTimeRange && centerTimeRange.from && centerTimeRange.to) {
            const centerFrom = centerTimeRange.from / 1000;
            const centerTo = centerTimeRange.to / 1000;
            const startIdx = candleData.findIndex(d => d.time >= centerFrom);
            let endIdx = startIdx;
            for (let i = startIdx; i < candleData.length; i++) {
                if (candleData[i].time <= centerTo) {
                    endIdx = i;
                } else {
                    break;
                }
            }
            if (startIdx >= 0 && endIdx >= startIdx && maxVisibleBars && maxVisibleBars > 0) {
                const mid = (startIdx + endIdx) / 2;
                const totalVisible = Math.min(maxVisibleBars, candleData.length);
                const halfVisible = Math.floor(totalVisible / 2);
                let from = Math.round(mid - halfVisible);
                let to = from + totalVisible - 1;
                const overflowLeft = -from;
                if (overflowLeft > 0) {
                    from += overflowLeft;
                    to += overflowLeft;
                }
                const overflowRight = to - (candleData.length - 1);
                if (overflowRight > 0) {
                    from -= overflowRight;
                    to -= overflowRight;
                }
                if (from < 0) from = 0;
                if (to >= candleData.length) to = candleData.length - 1;
                chartRef.current?.timeScale().setVisibleLogicalRange({from, to});
                return;
            }
            chartRef.current?.timeScale().fitContent();
            return;
        }

        if (disableHorizontalZoom) {
            chartRef.current?.timeScale().fitContent();
            return;
        }

        const hasFocusRange = focusTimeRange != null && focusTimeRange.from > 0 && focusTimeRange.to > 0;
        const shouldFreezeViewport = hasFocusRange && lockRightEdge && !hasForecastConnector;
        if (!shouldFreezeViewport) {
            if (preserveViewportOnDataUpdate && previousCount > 0) {
                const diff = candleData.length - previousCount;
                if (wasAtRightEdge && previousRange) {
                    // 自动跟随状态只平移、不改变当前显示根数。
                    chartRef.current?.timeScale().setVisibleLogicalRange({
                        from: previousRange.from + diff,
                        to: previousRange.to + diff,
                    });
                    return;
                }
                if (previousTimeRange) {
                    // 非跟随状态按时间恢复视口。无论本轮替换/新增多少根，都不得回到最右侧。
                    chartRef.current?.timeScale().setVisibleRange(previousTimeRange);
                    return;
                }
                if (previousRange) {
                    chartRef.current?.timeScale().setVisibleLogicalRange(previousRange);
                    return;
                }
            }

            if (chartRef.current && maxVisibleBars && maxVisibleBars > 0) {
                const visibleBars = Math.max(1, Math.min(maxVisibleBars, candleData.length));
                const to = candleData.length - 1 + (hasForecastConnector ? 2 : 0);
                const from = Math.max(0, to - visibleBars + 1);
                chartRef.current.timeScale().setVisibleLogicalRange({
                    from,
                    to,
                });
            }
            // 普通图表保持跟随最新K线；预测连线场景保留右侧未来空白。
            if (!hasForecastConnector) {
                chartRef.current?.timeScale().scrollToRealTime();
            }
        }
    }, [
        data,
        chartReady,
        maxVisibleBars,
        disableHorizontalZoom,
        preserveViewportOnDataUpdate,
        focusTimeRange,
        lockRightEdge,
        hasForecastConnector,
    ]);

    useEffect(() => {
        if (!chartReady || !chartRef.current || data.length === 0) return;
        const forecastEndTime = referenceLines?.forecastConnector?.endTime;
        if (!forecastEndTime) return;

        const lastBarTime = data[data.length - 1]?.time;
        if (!lastBarTime || forecastEndTime <= lastBarTime) return;

        const intervalMs = getTimeframeMs(timeFrame || '5m');
        const connectorPaddingBars = 2;
        const extraBars = Math.max(1, Math.ceil((forecastEndTime - lastBarTime) / intervalMs) + connectorPaddingBars);
        const currentRange = chartRef.current.timeScale().getVisibleLogicalRange();
        const visibleSpan = currentRange
            ? Math.max(10, currentRange.to - currentRange.from)
            : Math.max(20, Math.min(maxVisibleBars || 50, data.length + extraBars));
        const to = (data.length - 1) + extraBars;
        const from = Math.max(0, to - visibleSpan);
        chartRef.current.timeScale().setVisibleLogicalRange({from, to});
    }, [chartReady, data, maxVisibleBars, referenceLines?.forecastConnector?.endTime, timeFrame]);

    useEffect(() => {
        if (!chartReady || !comparisonSeriesRef.current) return;
        const comparisonCandles = [...(comparisonData || [])]
            .sort((a, b) => a.time - b.time)
            .filter(d => d != null && Number(d.high) >= Number(d.low) && Number(d.high) > 0)
            .map(d => ({
                time: toUTCTimestamp(d.time),
                open: Number(d.open),
                high: Number(d.high),
                low: Number(d.low),
                close: Number(d.close),
            }));
        safeSetData(comparisonSeriesRef.current, 'Comparison Candlestick', comparisonCandles);
        if (hasVisibleRangeLockRef.current) {
            applyPriceScaleToVisibleRange(chartRef.current?.timeScale().getVisibleRange() ?? null);
        }
        if (disableHorizontalZoom) {
            chartRef.current?.timeScale().fitContent();
        }
    }, [comparisonData, chartReady, disableHorizontalZoom, safeSetData, applyPriceScaleToVisibleRange]);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chartReady || !chart) return;

        const series = forecastBatchSeriesRef.current;
        if (!series) return;
        const byTime = new Map<number, ForecastBatchCandle[]>();
        for (const batch of comparisonBatches || []) {
            if (batch.visible === false) continue;
            const candles = [...batch.data]
                .sort((a, b) => a.time - b.time)
                .filter(d => d != null && Number(d.high) >= Number(d.low) && Number(d.high) > 0)
                .map(d => ({
                    time: Number(toUTCTimestamp(d.time)),
                    id: String(batch.id),
                    color: batch.color,
                    open: Number(d.open),
                    high: Number(d.high),
                    low: Number(d.low),
                    close: Number(d.close),
                    emphasized: batch.emphasized === true,
                }));
            candles.forEach(candle => {
                const slot = byTime.get(candle.time) || [];
                slot.push(candle);
                byTime.set(candle.time, slot);
            });
        }
        forecastBatchLookupRef.current = Object.fromEntries(byTime);
        const slots: ForecastSlotData[] = [...byTime.entries()]
            .sort(([left], [right]) => left - right)
            .map(([time, candles]) => ({
                time: time as UTCTimestamp,
                candles: candles.sort((left, right) => left.id.localeCompare(right.id, undefined, {numeric: true})),
            }));
        safeSetData(series, 'Forecast batches', slots);
    }, [comparisonBatches, chartReady, safeSetData]);

    useEffect(() => {
        if (!chartReady || !eventMarkersRef.current) return;
        const markers: SeriesMarker<Time>[] = (eventMarkers || [])
            .map(marker => ({
                ...marker,
                time: toUTCTimestamp(marker.time),
            }))
            .sort((a, b) => Number(a.time) - Number(b.time));
        eventMarkersRef.current.setMarkers(markers);
    }, [eventMarkers, chartReady]);

    useEffect(() => {
        if (!chartReady || !chartRef.current || !focusTimeRange) return;
        // 数据尚未加载时跳过，等待数据就绪后通过 data length 变化再次触发
        if (data.length === 0) {
            appliedFocusRangeRef.current = null;
            return;
        }
        // 相同的 focusTimeRange 值已应用过则跳过（避免轮询时重复触发）
        const key = `${focusTimeRange.from}_${focusTimeRange.to}`;
        if (appliedFocusRangeRef.current === key) return;
        chartRef.current.timeScale().setVisibleRange({
            from: toUTCTimestamp(focusTimeRange.from),
            to: toUTCTimestamp(focusTimeRange.to),
        });
        appliedFocusRangeRef.current = key;
    }, [focusTimeRange, chartReady, data.length]);

    useEffect(() => {
        if (!chartReady) return;

        // 设置更新标志，防止 subscribeCrosshairMove 回调触发 setState 导致无限循环
        isUpdatingRef.current = true;

        // 只清理 series，让 lightweight-charts 自动管理 pane
        Object.keys(indicatorSeriesRef.current).forEach(k => {
            const series = indicatorSeriesRef.current[k];
            // 只有当 series 存在且图表存在时才尝试移除
            if (series != null && chartRef.current) {
                try {
                    chartRef.current.removeSeries(series);
                } catch (e) {
                    // 忽略移除失败的错误（series 可能已经失效）
                    // console.debug('removeSeries error:', e);
                }
            }
        });
        indicatorSeriesRef.current = {};
        // 重置参考线标记，下一轮渲染重新添加
        refLinesAddedRef.current.clear();

        if (!candlestickSeriesRef.current) {
            // 重置更新标志，避免阻止后续的 crosshairMove 回调
            requestAnimationFrame(() => {
                isUpdatingRef.current = false;
            });
            return;
        }

        if (!indicators) {
            // 防御性清理：indicators 数据为空时也要清除残留副图。
            // useSerialPolling 周期内 API 偶发返回空 indicators 会 setIndicators(undefined)，
            // 若不在此分支清理 pane，series 已被上方清除但 chart 中的副图仍然残留，
            // 用户在 Track 弹窗等活跃轮询场景下逐个关闭指标后会看到空白 pane。
            // （参见历史修复 20260729-bot-chart-blank-subpane，该修复仅覆盖 visibleIndicators 切换路径。）
            if (chartRef.current) {
                while (chartRef.current.panes().length > 1) {
                    chartRef.current.removePane(chartRef.current.panes().length - 1);
                }
            }
            paneIndexMapRef.current = {};
            requestAnimationFrame(() => {
                isUpdatingRef.current = false;
            });
            return;
        }

        // 使用持久化的 paneIndexMapRef 分配 paneIndex
        // 从 visibleIndicators 提取需要显示的 oscillator 类型
        // 同时验证 indicators 中是否有对应的实际数据，避免创建空白副图
        const visibleOscillatorTypes = new Set<string>();
        Object.keys(visibleIndicators).forEach(key => {
            if (visibleIndicators[key]) {
                const baseType = key.split('_')[0];
                if (['MACD', 'KDJ', 'RSI', 'CCI', 'OBV', 'ADX'].includes(baseType)) {
                    // 检查 indicators 中是否有该类型的数据
                    const hasData = Object.keys(indicators || {}).some(indicatorKey => {
                        const indicatorBaseType = indicatorKey.split('_')[0];
                        return indicatorBaseType === baseType;
                    });
                    if (hasData) {
                        visibleOscillatorTypes.add(baseType);
                    }
                }
            }
        });
        // 清理已取消的指标 - 先收集需要移除的pane索引
        const panesToRemove: number[] = [];
        Object.keys(paneIndexMapRef.current).forEach(type => {
            if (!visibleOscillatorTypes.has(type)) {
                const paneIndex = paneIndexMapRef.current[type];
                if (paneIndex !== undefined && paneIndex > 0) {
                    panesToRemove.push(paneIndex);
                }
                delete paneIndexMapRef.current[type];
            }
        });

        // 移除空的pane（从后往前移除，避免索引变化影响）
        if (chartRef.current && panesToRemove.length > 0) {
            // 去重并倒序排序
            const uniquePanes = [...new Set(panesToRemove)].sort((a, b) => b - a);
            uniquePanes.forEach(paneIndex => {
                try {
                    // 不能移除主图(paneIndex=0)
                    if (paneIndex > 0 && paneIndex < chartRef.current!.panes().length) {
                        chartRef.current!.removePane(paneIndex);
                    }
                } catch (e) {
                    // console.debug('removePane error:', e);
                }
            });

            // 重要：移除pane后，需要清除所有振荡指标的series引用
            // 因为这些series可能已经失效（被一起移除了）
            Object.keys(indicatorSeriesRef.current).forEach(key => {
                const baseType = key.split('_')[0];
                if (['MACD', 'KDJ', 'RSI', 'CCI', 'OBV', 'ADX'].includes(baseType)) {
                    delete indicatorSeriesRef.current[key];
                }
            });
        }

        // 重新压缩pane索引（因为removePane后索引会重新排列）
        const remainingTypes = Object.keys(paneIndexMapRef.current);
        const newPaneIndexMap: Record<string, number> = {};
        remainingTypes.forEach((type, index) => {
            newPaneIndexMap[type] = index + 1;  // 副图从1开始
        });
        paneIndexMapRef.current = newPaneIndexMap;

        // 为新指标分配 paneIndex
        visibleOscillatorTypes.forEach(type => {
            if (paneIndexMapRef.current[type] === undefined) {
                paneIndexMapRef.current[type] = Object.keys(paneIndexMapRef.current).length + 1;
            }
        });

        // 确保需要的 pane 存在
        if (chartRef.current) {
            const currentPaneCount = chartRef.current.panes().length;
            const neededPaneCount = Math.max(...Object.values(paneIndexMapRef.current), 0) + 1;
            for (let i = currentPaneCount; i < neededPaneCount; i++) {
                chartRef.current.addPane();
            }
        }

        // 防御性清理：当没有任何可见振荡指标时，强制移除所有副图 pane
        // 防止因竞态条件导致空 pane 残留
        if (0 === visibleOscillatorTypes.size && chartRef.current) {
            while (chartRef.current.panes().length > 1) {
                chartRef.current.removePane(chartRef.current.panes().length - 1);
            }
            paneIndexMapRef.current = {};
        }

        // 预处理：按指标类型分组，对同类型的 key 按周期数字升序排列，确定各 key 的颜色索引
        const colorIndexByKey: Record<string, number> = {};
        const keysByBaseType: Record<string, string[]> = {};
        Object.keys(indicators).forEach(k => {
            const bt = k.split('_')[0];
            if (!keysByBaseType[bt]) keysByBaseType[bt] = [];
            keysByBaseType[bt].push(k);
        });
        Object.values(keysByBaseType).forEach(keys => {
            // 从 key（如 EMA_ema_12）中解析周期数字，按升序排列后赋予颜色索引
            keys.sort((a, b) => {
                const pa = parseInt(a.replace(/^[A-Z]+_[a-z]+_/i, ''));
                const pb = parseInt(b.replace(/^[A-Z]+_[a-z]+_/i, ''));
                return (isNaN(pa) ? 0 : pa) - (isNaN(pb) ? 0 : pb);
            }).forEach((k, idx) => {
                colorIndexByKey[k] = idx;
            });
        });

        Object.entries(indicators).forEach(([key, values]) => {
            if (!values || !Array.isArray(values) || values.length === 0) {
                return;
            }

            const baseType = key.split('_')[0];
            const periodWithPrefix = key.substring(baseType.length + 1);
            const period = periodWithPrefix.replace(/^(ema|sma|wma|rsi|boll|macd|kdj|cci|atr|obv|adx)_/i, '').replace(/,/g, '_');
            const visibleKey = `${baseType}_${period}`;
            const isVisible = visibleIndicators[visibleKey] !== undefined
                ? visibleIndicators[visibleKey]
                : (visibleIndicators[`${baseType}_${period.split(/[_,]/)[0]}`] ?? false);
            if (!isVisible) return;

            const isOscillator = ['MACD', 'KDJ', 'RSI', 'CCI', 'OBV', 'ADX'].includes(baseType);
            let paneIndex = 0; // default main pane

            if (isOscillator) {
                paneIndex = paneIndexMapRef.current[baseType] ?? 0;
            }

            if (baseType === 'MACD') {
                // MACD三线：DIF橙红、DEA深紫、柱状图绿/红
                if (!indicatorSeriesRef.current[`${key}_DIF`]) {
                    indicatorSeriesRef.current[`${key}_DIF`] = chartRef.current?.addSeries(LineSeries, {
                        color: '#4d9fff',
                        lineWidth: 1,
                        priceLineVisible: false,
                        lastValueVisible: false,
                        priceScaleId: 'right',
                    }, paneIndex) || null;
                }
                safeSetData(indicatorSeriesRef.current[`${key}_DIF`], `MACD_${key}_DIF`, toLineSeriesData(values, 'diff'));

                if (!indicatorSeriesRef.current[`${key}_DEA`]) {
                    indicatorSeriesRef.current[`${key}_DEA`] = chartRef.current?.addSeries(LineSeries, {
                        color: '#f5a623',
                        lineWidth: 1,
                        priceLineVisible: false,
                        lastValueVisible: false,
                        priceScaleId: 'right',
                    }, paneIndex) || null;
                }
                safeSetData(indicatorSeriesRef.current[`${key}_DEA`], `MACD_${key}_DEA`, toLineSeriesData(values, 'signal'));

                if (!indicatorSeriesRef.current[key]) {
                    indicatorSeriesRef.current[key] = chartRef.current?.addSeries(HistogramSeries, {
                        priceFormat: {
                            type: 'price',
                            precision: 4,
                            minMove: 0.0001,
                        },
                        priceScaleId: 'right',
                        priceLineVisible: false,
                        lastValueVisible: false,
                    }, paneIndex) || null;
                }
                safeSetData(indicatorSeriesRef.current[key], `MACD_${key}_histogram`, toHistogramSeriesData(values));
                indicatorSeriesRef.current[`${key}_DIF`]?.priceScale().applyOptions({autoScale: true});
                indicatorSeriesRef.current[`${key}_DEA`]?.priceScale().applyOptions({autoScale: true});
                indicatorSeriesRef.current[key]?.priceScale().applyOptions({autoScale: true});
                return;
            }

            if (baseType === 'BOLL') {
                // BOLL三轨使用分配色（中轨实线，上下轨半透明）
                const bollColor = indicatorColorMap?.[visibleKey] || '#f5222d';
                if (!indicatorSeriesRef.current[`${key}_upper`]) {
                    indicatorSeriesRef.current[`${key}_upper`] = chartRef.current?.addSeries(LineSeries, {
                        color: bollColor + '66', lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
                    }, paneIndex) || null;
                }
                safeSetData(indicatorSeriesRef.current[`${key}_upper`], `BOLL_${key}_upper`, toLineSeriesData(values, 'upper'));

                if (!indicatorSeriesRef.current[`${key}_middle`]) {
                    indicatorSeriesRef.current[`${key}_middle`] = chartRef.current?.addSeries(LineSeries, {
                        color: bollColor, lineWidth: 1, lineStyle: 2, priceLineVisible: false, lastValueVisible: false,
                    }, paneIndex) || null;
                }
                safeSetData(indicatorSeriesRef.current[`${key}_middle`], `BOLL_${key}_middle`, toLineSeriesData(values, 'middle'));

                if (!indicatorSeriesRef.current[`${key}_lower`]) {
                    indicatorSeriesRef.current[`${key}_lower`] = chartRef.current?.addSeries(LineSeries, {
                        color: bollColor + '66', lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
                    }, paneIndex) || null;
                }
                safeSetData(indicatorSeriesRef.current[`${key}_lower`], `BOLL_${key}_lower`, toLineSeriesData(values, 'lower'));
                return;
            }

            if (baseType === 'KDJ') {
                // 每个 KDJ 周期使用独立色系，K/D/J 在同色系内明暗区分
                const kdjColorSet = KDJ_COLOR_SETS[colorIndexByKey[key] % KDJ_COLOR_SETS.length] || KDJ_COLOR_SETS[0];
                if (!indicatorSeriesRef.current[`${key}_K`]) {
                    indicatorSeriesRef.current[`${key}_K`] = chartRef.current?.addSeries(LineSeries, {
                        color: kdjColorSet.K, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
                    }, paneIndex) || null;
                }
                safeSetData(indicatorSeriesRef.current[`${key}_K`], `KDJ_${key}_K`, toLineSeriesData(values, 'k'));

                if (!indicatorSeriesRef.current[`${key}_D`]) {
                    indicatorSeriesRef.current[`${key}_D`] = chartRef.current?.addSeries(LineSeries, {
                        color: kdjColorSet.D, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
                    }, paneIndex) || null;
                }
                safeSetData(indicatorSeriesRef.current[`${key}_D`], `KDJ_${key}_D`, toLineSeriesData(values, 'd'));

                if (!indicatorSeriesRef.current[`${key}_J`]) {
                    indicatorSeriesRef.current[`${key}_J`] = chartRef.current?.addSeries(LineSeries, {
                        color: kdjColorSet.J, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
                    }, paneIndex) || null;
                }
                safeSetData(indicatorSeriesRef.current[`${key}_J`], `KDJ_${key}_J`, toLineSeriesData(values, 'j'));
                return;
            }

            // 优先使用传入的颜色映射，否则按周期索引从颜色数组中选色
            const colorFromMap = indicatorColorMap?.[visibleKey];
            const colorIdx = colorIndexByKey[key] ?? 0;
            const multiColors = MULTI_PERIOD_COLORS[baseType];
            const lineColor = colorFromMap || (multiColors
                ? multiColors[colorIdx % multiColors.length]
                : (['#F0B429', '#00d97e', '#00d97e', '#f5a623'][colorIdx % 4]));

            if (!indicatorSeriesRef.current[key]) {
                const series = chartRef.current?.addSeries(LineSeries, {
                    color: lineColor,
                    lineWidth: 1,
                    priceLineVisible: false,
                    lastValueVisible: false,
                }, paneIndex);

                if (series) {
                    indicatorSeriesRef.current[key] = series;
                }
            }

            const series = indicatorSeriesRef.current[key];
            if (series) {
                safeSetData(series, `${key}_value`, toLineSeriesData(values, 'value'));
                // RSI 超买超卖参考线（每个 pane 只添加一次）
                if ('RSI' === baseType && !refLinesAddedRef.current.has('RSI')) {
                    refLinesAddedRef.current.add('RSI');
                    series.createPriceLine({
                        price: 70,
                        color: '#ff5252',
                        lineWidth: 1,
                        lineStyle: 2,
                        axisLabelVisible: true,
                        title: '超买'
                    });
                    series.createPriceLine({
                        price: 30,
                        color: '#00d97e',
                        lineWidth: 1,
                        lineStyle: 2,
                        axisLabelVisible: true,
                        title: '超卖'
                    });
                    series.createPriceLine({
                        price: 50,
                        color: 'rgba(255,255,255,0.2)',
                        lineWidth: 1,
                        lineStyle: 2,
                        axisLabelVisible: false
                    });
                }
            }
        });

        // 重置更新标志
        requestAnimationFrame(() => {
            isUpdatingRef.current = false;
        });
    }, [indicators, visibleIndicators, chartReady, data]);

    useEffect(() => {
        if (!chartReady || !candlestickSeriesRef.current) return;

        // 设置更新标志，防止 subscribeCrosshairMove 回调触发 setState 导致无限循环
        isUpdatingRef.current = true;

        const series = candlestickSeriesRef.current;

        // 如果强制清除，则尝试移除所有可能的价格线
        if (debugForceClearPriceLines) {
            const allLines = [
                ...priceLineRefs.current,
                markPriceLineRef.current ? [markPriceLineRef.current] : [],
            ].flat();
            allLines.forEach((line) => {
                try {
                    series.removePriceLine(line);
                } catch (e) {
                    // 忽略移除失败的错误
                }
            });
            priceLineRefs.current = [];
            markPriceLineRef.current = null;
        } else {
            // 正常清理逻辑
            const allLines = [
                ...priceLineRefs.current,
                markPriceLineRef.current ? [markPriceLineRef.current] : [],
            ].flat();

            allLines.forEach((line) => {
                try {
                    series.removePriceLine(line);
                } catch (e) {
                    // 忽略移除失败的错误
                }
            });
            priceLineRefs.current = [];
            markPriceLineRef.current = null;
        }
        // 清理开仓价水平线（有时间范围限制的 LineSeries）
        if (entryLineSeriesRef.current) {
            try {
                chartRef.current?.removeSeries(entryLineSeriesRef.current);
            } catch (e) {
                // 忽略移除失败的错误
            }
            entryLineSeriesRef.current = null;
        }
        // 清理实时价格越界补偿线
        if (markPriceExtensionSeriesRef.current) {
            try {
                chartRef.current?.removeSeries(markPriceExtensionSeriesRef.current);
            } catch (e) {
                // 忽略移除失败的错误
            }
            markPriceExtensionSeriesRef.current = null;
        }
        // 清理支撑阻力线
        supportResistanceLineRefs.current.forEach(lineSeries => {
            try {
                chartRef.current?.removeSeries(lineSeries);
            } catch (e) {
                // 忽略移除失败的错误
            }
        });
        supportResistanceLineRefs.current = [];

        // 清理支撑阻力线价格标签
        supportResistancePriceLineRefs.current.forEach(line => {
            try {
                series.removePriceLine(line);
            } catch (e) {
                // 忽略移除失败的错误
            }
        });
        supportResistancePriceLineRefs.current = [];

        const entries = Object.entries(referenceLines || {}).filter(([, v]) => v !== undefined);

        const priceSeen = new Set<number>();
        const linesToCreate: { price: number; color: string; key: string }[] = [];

        entries.forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            // 支撑/阻力线是数组，由下方专用逻辑处理。Number([]) 会得到 0，
            // 若在这里按普通价格处理，会错误创建一条 0 价格水平线并压缩主图。
            if (key === 'supportLines' || key === 'resistanceLines' || key === 'forecastConnector') return;
            // avgPx 和 closePx 需要特殊处理（时间范围限制），跳过此处
            if (key === 'avgPx' || key === 'cTime' || key === 'uTime') return;

            // 当 showEntryLine=false 时，跳过 closePx 的水平线（历史持仓只显示标签）
            if (key === 'closePx' && !showEntryLine) return;
            // forecastConnector 模式下，模型目标止盈不再单独画水平线，避免和斜线重复表达
            if (key === 'targetTakeProfitPx' && referenceLines?.forecastConnector) return;

            // 将价格转换为数字类型
            const numericPrice = typeof value === 'string' ? parseFloat(value) : Number(value);
            if (!Number.isFinite(numericPrice) || numericPrice <= 0) return;
            if (priceSeen.has(numericPrice)) return;

            const colors: Record<string, string> = {
                avgPx: '#F0B429',        // 金色
                closePx: '#f5a623',      // 橙色
                takeProfitPx: '#00d97e', // 绿色（止盈）
                targetTakeProfitPx: '#13c2c2', // 青色（模型目标止盈）
                stopLossPx: '#ff5252',   // 红色（止损）
                liquidationPx: '#a78bfa',// 紫色（强平价）
            };

            priceSeen.add(numericPrice);
            linesToCreate.push({
                price: numericPrice,
                color: colors[key] || '#f5a623',
                key
            });
        });

        // 处理支撑线和阻力线（使用 LineSeries 绘制，对齐x轴）
        // 获取图表最后一个 bar 的时间
        const lastBarTime = data.length > 0 ? data[data.length - 1].time : 0;
        const firstBarTime = data.length > 0 ? data[0].time : 0;

        // 调试日志
        // console.log('[支撑/阻力线] K线数据时间范围:', {
        //     firstBarTime: firstBarTime,
        //     firstBarDate: new Date(firstBarTime).toISOString(),
        //     lastBarTime: lastBarTime,
        //     lastBarDate: new Date(lastBarTime).toISOString(),
        //     barsCount: data.length
        // });

        // 在 K 线数据中找到最接近目标时间的 bar 的时间戳
        // 注意：K线数据的 time 字段和 targetTime 都是13位毫秒时间戳
        const findClosestBarTime = (targetTime: number): number => {
            if (data.length === 0) return targetTime;
            let closest = data[0].time;
            let minDiff = Math.abs(data[0].time - targetTime);
            for (let i = 1; i < data.length; i++) {
                const diff = Math.abs(data[i].time - targetTime);
                if (diff < minDiff) {
                    minDiff = diff;
                    closest = data[i].time;
                }
            }
            return closest;
        };

        // 绘制支撑线（蓝色）
        const supportLines = referenceLines?.supportLines;
        if (supportLines && Array.isArray(supportLines) && chartRef.current && data.length > 0) {
            // console.log('[支撑线] 待绘制数量:', supportLines.length);
            supportLines.forEach((line, index) => {
                const lineData = typeof line === 'object' ? line : {
                    price: line,
                    startTime: undefined,
                    endTime: undefined
                };
                const numericPrice = typeof lineData.price === 'string' ? parseFloat(lineData.price) : Number(lineData.price);
                if (!Number.isFinite(numericPrice)) return;

                // 检查时间范围是否在K线数据内，如果不在则跳过
                if (lineData.startTime && lineData.startTime > lastBarTime) {
                    // console.log(`[支撑线 ${index}] 跳过: startTime 晚于 K线数据 (${new Date(lineData.startTime).toISOString()} > ${new Date(lastBarTime).toISOString()})`);
                    return;
                }
                if (lineData.endTime && lineData.endTime < firstBarTime) {
                    // console.log(`[支撑线 ${index}] 跳过: endTime 早于 K线数据 (${new Date(lineData.endTime).toISOString()} < ${new Date(firstBarTime).toISOString()})`);
                    return;
                }

                let startTime = lineData.startTime ? findClosestBarTime(lineData.startTime) : firstBarTime;
                // 使用 endTime 或默认到 lastBarTime
                let endTime = lineData.endTime ? findClosestBarTime(lineData.endTime) : lastBarTime;

                // 确保 startTime <= endTime
                if (startTime > endTime) {
                    [startTime, endTime] = [endTime, startTime];
                }

                // 调试日志
                // console.log(`[支撑线 ${index}]`, {
                //     price: numericPrice,
                //     startTime: lineData.startTime,
                //     startTimeDate: lineData.startTime ? new Date(lineData.startTime).toISOString() : 'undefined',
                //     matchedStartTime: startTime,
                //     matchedStartDate: new Date(startTime).toISOString(),
                //     endTime: lineData.endTime,
                //     endTimeDate: lineData.endTime ? new Date(lineData.endTime).toISOString() : 'undefined',
                //     matchedEndTime: endTime,
                //     matchedEndDate: new Date(endTime).toISOString()
                // });

                const supportLineSeries = chartRef.current!.addSeries(LineSeries, {

                    color: '#00d97e',  // 支撑线青色

                    lineWidth: 1,

                    lineStyle: 2,  // 虚线

                    priceLineVisible: false,  // 不显示价格线标签（避免重复）

                    lastValueVisible: false,  // 不显示末端标签（避免重复）

                });

                if (startTime === endTime) {
                    supportLineSeries.setData([
                        {time: toUTCTimestamp(startTime), value: numericPrice},
                    ]);
                } else {
                    supportLineSeries.setData([
                        {time: toUTCTimestamp(startTime), value: numericPrice},
                        {time: toUTCTimestamp(endTime), value: numericPrice},
                    ]);
                }

                supportResistanceLineRefs.current.push(supportLineSeries);

                // 创建 Y 轴标签显示 S1/S2/S3
                if (lineData.label && candlestickSeriesRef.current) {
                    const labelPriceLine = candlestickSeriesRef.current.createPriceLine({
                        price: numericPrice,
                        color: '#F0B429',
                        lineVisible: false,
                        lineStyle: 2,
                        axisLabelVisible: true,
                        title: lineData.label,  // S1, S2, S3
                    });
                    if (labelPriceLine) {
                        supportResistancePriceLineRefs.current.push(labelPriceLine);
                    }
                }
            });
        }

        // 绘制阻力线（橙色）
        const resistanceLines = referenceLines?.resistanceLines;
        if (resistanceLines && Array.isArray(resistanceLines) && chartRef.current && data.length > 0) {
            // console.log('[阻力线] 待绘制数量:', resistanceLines.length);
            resistanceLines.forEach((line, index) => {
                const lineData = typeof line === 'object' ? line : {
                    price: line,
                    startTime: undefined,
                    endTime: undefined
                };
                const numericPrice = typeof lineData.price === 'string' ? parseFloat(lineData.price) : Number(lineData.price);
                if (!Number.isFinite(numericPrice)) return;

                // 检查时间范围是否在K线数据内，如果不在则跳过
                if (lineData.startTime && lineData.startTime > lastBarTime) {
                    // console.log(`[阻力线 ${index}] 跳过: startTime 晚于 K线数据 (${new Date(lineData.startTime).toISOString()} > ${new Date(lastBarTime).toISOString()})`);
                    return;
                }
                if (lineData.endTime && lineData.endTime < firstBarTime) {
                    // console.log(`[阻力线 ${index}] 跳过: endTime 早于 K线数据 (${new Date(lineData.endTime).toISOString()} < ${new Date(firstBarTime).toISOString()})`);
                    return;
                }

                let startTime = lineData.startTime ? findClosestBarTime(lineData.startTime) : firstBarTime;
                // 使用 endTime 或默认到 lastBarTime
                let endTime = lineData.endTime ? findClosestBarTime(lineData.endTime) : lastBarTime;

                // 确保 startTime <= endTime
                if (startTime > endTime) {
                    [startTime, endTime] = [endTime, startTime];
                }

                // 调试日志
                // console.log(`[阻力线 ${index}]`, {
                //     price: numericPrice,
                //     startTime: lineData.startTime,
                //     startTimeDate: lineData.startTime ? new Date(lineData.startTime).toISOString() : 'undefined',
                //     matchedStartTime: startTime,
                //     matchedStartDate: new Date(startTime).toISOString(),
                //     endTime: lineData.endTime,
                //     endTimeDate: lineData.endTime ? new Date(lineData.endTime).toISOString() : 'undefined',
                //     matchedEndTime: endTime,
                //     matchedEndDate: new Date(endTime).toISOString()
                // });

                const resistanceLineSeries = chartRef.current!.addSeries(LineSeries, {
                    color: '#f5a623',  // 阻力线橙色
                    lineWidth: 1,
                    lineStyle: 2,  // 虚线
                    priceLineVisible: false,  // 不显示价格线标签（避免重复）
                    lastValueVisible: false,  // 不显示末端标签（避免重复）
                });

                if (startTime === endTime) {
                    resistanceLineSeries.setData([
                        {time: toUTCTimestamp(startTime), value: numericPrice},
                    ]);
                } else {
                    resistanceLineSeries.setData([
                        {time: toUTCTimestamp(startTime), value: numericPrice},
                        {time: toUTCTimestamp(endTime), value: numericPrice},
                    ]);
                }

                supportResistanceLineRefs.current.push(resistanceLineSeries);

                // 创建 Y 轴标签显示 R1/R2/R3
                if (lineData.label && candlestickSeriesRef.current) {
                    const labelPriceLine = candlestickSeriesRef.current.createPriceLine({
                        price: numericPrice,
                        color: '#f5a623',
                        lineVisible: false,
                        lineStyle: 2,
                        axisLabelVisible: true,
                        title: lineData.label,  // R1, R2, R3
                    });
                    if (labelPriceLine) {
                        supportResistancePriceLineRefs.current.push(labelPriceLine);
                    }
                }
            });
        }

        const markPriceValue = markPrice;
        if (markPriceValue !== undefined && markPriceValue !== null && !priceSeen.has(markPriceValue)) {
            const numericMarkPrice = typeof markPriceValue === 'string' ? parseFloat(markPriceValue) : Number(markPriceValue);
            if (Number.isFinite(numericMarkPrice)) {
                linesToCreate.push({
                    price: numericMarkPrice,
                    color: markPriceColor || '#f5a623',
                    key: 'markPrice'
                });
            }
        }

        // 实时价格越界补偿：若 markPrice 超出最后一根 K 线范围，从收盘价到 markPrice 绘制虚线
        const markPriceForExt = markPrice;
        if (markPriceForExt !== undefined && markPriceForExt !== null && data.length > 0 && chartRef.current) {
            const lastBar = data[data.length - 1];
            const numericVal = typeof markPriceForExt === 'string' ? parseFloat(markPriceForExt) : Number(markPriceForExt);
            if (Number.isFinite(numericVal) && (numericVal > lastBar.high || numericVal < lastBar.low)) {
                const tfMs = getTimeframeMs(timeFrame || '1h');
                const nextTime = lastBar.time + tfMs;
                const extColor = markPriceColor || '#f5a623';

                const extSeries = chartRef.current.addSeries(LineSeries, {
                    color: extColor,
                    lineWidth: 1,
                    lineStyle: 2,
                    priceLineVisible: false,
                    lastValueVisible: false,
                });
                extSeries.setData([
                    {time: toUTCTimestamp(lastBar.time), value: lastBar.close},
                    {time: toUTCTimestamp(nextTime), value: numericVal},
                ]);
                markPriceExtensionSeriesRef.current = extSeries;
            }
        }

        // 创建有时间范围限制的开仓价水平线（从开仓时间开始，不是横贯全图）
        // 注意：此逻辑必须在 "if (linesToCreate.length === 0) return;" 之前执行
        const avgPx = referenceLines?.avgPx;
        const closePx = referenceLines?.closePx;
        const cTime = referenceLines?.cTime;

        if (avgPx !== undefined && cTime !== undefined && chartRef.current && data.length > 0) {
            // 获取图表最后一个 bar 的时间
            const lastBarTime = data[data.length - 1].time;

            // 在 K 线数据中找到最接近 cTime 的 bar 的时间戳
            // 因为 K 线数据的时间戳是对齐到时间帧的，cTime 需要找到对应的 bar
            const findClosestBarTime = (targetTime: number): number => {
                let closest = data[0].time;
                let minDiff = Math.abs(data[0].time - targetTime);
                for (let i = 1; i < data.length; i++) {
                    const diff = Math.abs(data[i].time - targetTime);
                    if (diff < minDiff) {
                        minDiff = diff;
                        closest = data[i].time;
                    }
                }
                return closest;
            };

            const startTime = findClosestBarTime(cTime);

            // 只有当 showEntryLine 为 true 且 showAvgPx 为 true 时才创建 LineSeries 绘制水平线
            if (showEntryLine && showAvgPx) {
                // 创建新的 LineSeries
                const entryLineSeries = chartRef.current.addSeries(LineSeries, {
                    color: '#F0B429',
                    lineWidth: 1,
                    lineStyle: 2,
                    priceLineVisible: false,
                    lastValueVisible: false,
                });

                // 添加数据点：开仓时间到最后一个 bar 时间
                // 注意：如果 startTime 和 lastBarTime 相同，只能设置一个点，否则会报重复时间戳错误
                if (startTime === lastBarTime) {
                    entryLineSeries.setData([
                        {time: toUTCTimestamp(startTime), value: avgPx},
                    ]);
                } else {
                    entryLineSeries.setData([
                        {time: toUTCTimestamp(startTime), value: avgPx},
                        {time: toUTCTimestamp(lastBarTime), value: avgPx},
                    ]);
                }

                entryLineSeriesRef.current = entryLineSeries;
            }
        }

        // 重要：先清空 refs，避免重复添加同一条线
        priceLineRefs.current = [];
        markPriceLineRef.current = null;

        // 创建开仓价格的Y轴标签（在清空后创建，避免被清除）
        // showAvgPx=true 时才显示Y轴标签
        if (avgPx !== undefined && showAvgPx) {
            const entryPriceLine = series.createPriceLine({
                price: avgPx,
                color: '#F0B429',
                lineVisible: false,
                lineStyle: 2,
                axisLabelVisible: true,
                title: '开',
            });
            if (entryPriceLine) {
                priceLineRefs.current.push(entryPriceLine);
            }
        }

        // 创建平仓价格的Y轴标签（历史持仓时showEntryLine=false，只显示标签不显示线）
        // showClosePx=true 时才显示Y轴标签
        if (!showEntryLine && closePx !== undefined && showClosePx) {
            const closePriceLine = series.createPriceLine({
                price: closePx,
                color: '#f5a623',
                lineVisible: false,
                lineStyle: 2,
                axisLabelVisible: true,
                title: '平',
            });
            if (closePriceLine) {
                priceLineRefs.current.push(closePriceLine);
            }
        }

        if (linesToCreate.length === 0) {
            // Track 弹窗默认只显示开仓价标签，可能没有普通价格线可创建。
            // 必须释放更新锁，否则后续所有 crosshairMove 都会被永久丢弃。
            // 同时解除 Y 轴可视范围锁定，恢复库原生 autoScale（2026-08-01）
            hasVisibleRangeLockRef.current = false;
            chartRef.current?.priceScale('right').setAutoScale(true);
            requestAnimationFrame(() => {
                isUpdatingRef.current = false;
            });
            return;
        }

        linesToCreate.sort((a, b) => a.price - b.price);

        linesToCreate.forEach(({price, color, key}) => {
            const line = series.createPriceLine({
                price,
                color,
                lineWidth: 1,
                lineStyle: 2,
                axisLabelVisible: true,
            });
            if (line) {
                if (key === 'markPrice') {
                    markPriceLineRef.current = line;
                } else {
                    priceLineRefs.current.push(line);
                }
            }
        });

        // 锁定 Y 轴为"当前可视窗口"范围（可视K线 + 指标 + 参考线），
        // 水平拖动/缩放时由 visibleTimeRangeChange 订阅驱动重算（2026-08-01）
        hasVisibleRangeLockRef.current = true;
        applyPriceScaleToVisibleRange(chartRef.current?.timeScale().getVisibleRange() ?? null);

        // 使用 requestAnimationFrame 延迟重置标志，确保所有图表操作完成
        requestAnimationFrame(() => {
            isUpdatingRef.current = false;
        });

        return () => {
            isUpdatingRef.current = false;
        };
    }, [referenceLines, markPrice, markPriceColor, chartReady, data, comparisonData, indicators, visibleIndicators, showEntryLine, showAvgPx, showClosePx, timeFrame, applyPriceScaleToVisibleRange]);

    // 格式化数字
    const formatNumber = (num: number, decimals: number = 2): string => {
        if (Number.isFinite(num)) {
            return num.toFixed(decimals);
        }
        return '-';
    };

    /**
     * 渲染 tooltip 中的指标行
     * 仅渲染已勾选的指标，颜色与图线保持一致
     */
    const renderIndicatorRows = (): React.ReactNode => {
        if (!tooltipIndicators) return null;

        const colorIndexMap = indicatorColorIndexRef.current;
        const rows: React.ReactNode[] = [];

        Object.entries(visibleIndicators).forEach(([visKey, isVisible]) => {
            if (!isVisible) return;

            // visKey 格式：'EMA_12'、'RSI_14'、'BOLL_20'、'MACD_12'、'KDJ_9'
            const underscoreIdx = visKey.indexOf('_');
            if (underscoreIdx < 0) return;
            const baseType = visKey.substring(0, underscoreIdx); // 'EMA'
            const period = visKey.substring(underscoreIdx + 1);  // '12'

            // 在 tooltipIndicators 中查找匹配的 key（格式如 EMA_ema_12）
            const matchingKey = Object.keys(tooltipIndicators).find(k => {
                if (k.split('_')[0] !== baseType) return false;
                // 去掉 "TYPE_prefix_" 后的剩余部分为周期
                const stripped = k.replace(new RegExp(`^${baseType}_[a-z]+_`, 'i'), '');
                // visibleKey 用下划线（如 12_26_9），数据用逗号（如 12,26,9），统一用逗号比较
                const normalizedPeriod = period.replace(/_/g, ',');
                return stripped === normalizedPeriod || stripped.startsWith(normalizedPeriod + '_') || stripped.startsWith(normalizedPeriod + ',');
            });
            if (!matchingKey) return;

            const pt = tooltipIndicators[matchingKey];
            if (!pt) return;

            const colorIdx = colorIndexMap[matchingKey] ?? 0;

            if (baseType === 'EMA' || baseType === 'SMA' || baseType === 'WMA' || baseType === 'ATR') {
                // 单值指标，颜色按周期索引
                const colors = MULTI_PERIOD_COLORS[baseType];
                const color = colors ? colors[colorIdx % colors.length] : '#999';
                if (pt.value === undefined) return;
                rows.push(
                    <div key={visKey} style={{display: 'flex', justifyContent: 'space-between', gap: 8}}>
                        <span style={{color}}>{baseType}({indicatorPeriodLabels?.[visKey] || period}):</span>
                        <span style={{color}}>{formatNumber(pt.value, 4)}</span>
                    </div>
                );
            } else if (baseType === 'RSI' || baseType === 'CCI' || baseType === 'OBV') {
                // 振荡类单值指标
                const colors = MULTI_PERIOD_COLORS[baseType];
                const color = colors ? colors[colorIdx % colors.length] : '#999';
                if (pt.value === undefined) return;
                rows.push(
                    <div key={visKey} style={{display: 'flex', justifyContent: 'space-between', gap: 8}}>
                        <span style={{color}}>{baseType}({indicatorPeriodLabels?.[visKey] || period}):</span>
                        <span style={{color}}>{formatNumber(pt.value, 2)}</span>
                    </div>
                );
            } else if (baseType === 'ADX') {
                // ADX 指标
                const colors = MULTI_PERIOD_COLORS.ADX;
                const color = colors ? colors[colorIdx % colors.length] : '#f5222d';
                if (pt.value === undefined) return;
                rows.push(
                    <div key={visKey} style={{display: 'flex', justifyContent: 'space-between', gap: 8}}>
                        <span style={{color}}>ADX({indicatorPeriodLabels?.[visKey] || period}):</span>
                        <span style={{color}}>{formatNumber(pt.value, 2)}</span>
                    </div>
                );
            } else if (baseType === 'BOLL') {
                // 三轨颜色固定：上红、中黄、下绿
                if (pt.upper === undefined && pt.middle === undefined && pt.lower === undefined) return;
                rows.push(
                    <div key={visKey} style={{fontSize: 11}}>
                        <span style={{color: '#999'}}>BOLL({indicatorPeriodLabels?.[visKey] || period}): </span>
                        <span style={{color: '#f5222d'}}>{formatNumber(pt.upper ?? NaN, 4)}</span>
                        <span style={{color: '#555'}}>/</span>
                        <span style={{color: '#f5a623'}}>{formatNumber(pt.middle ?? NaN, 4)}</span>
                        <span style={{color: '#555'}}>/</span>
                        <span style={{color: '#00d97e'}}>{formatNumber(pt.lower ?? NaN, 4)}</span>
                    </div>
                );
            } else if (baseType === 'MACD') {
                // DIF橙红、DEA深紫、直方图绿/红
                rows.push(
                    <div key={visKey} style={{fontSize: 11}}>
                        <span
                            style={{color: '#999'}}>MACD({indicatorPeriodLabels?.[visKey] || period.replace(/_/g, ',')}): </span>
                        <span style={{color: '#fa541c'}}>D:{formatNumber(pt.diff ?? NaN, 4)}</span>
                        <span style={{color: '#555'}}> </span>
                        <span style={{color: '#a78bfa'}}>S:{formatNumber(pt.signal ?? NaN, 4)}</span>
                        <span style={{color: '#555'}}> </span>
                        <span
                            style={{color: pt.histogram !== undefined && pt.histogram >= 0 ? '#00d97e' : '#ff5252'}}>H:{formatNumber(pt.histogram ?? NaN, 4)}</span>
                    </div>
                );
            } else if (baseType === 'KDJ') {
                // 每个周期独立色系
                const kdjSet = KDJ_COLOR_SETS[colorIdx % KDJ_COLOR_SETS.length];
                rows.push(
                    <div key={visKey} style={{fontSize: 11}}>
                        <span style={{color: '#999'}}>KDJ({indicatorPeriodLabels?.[visKey] || period}): </span>
                        <span style={{color: kdjSet.K}}>K:{formatNumber(pt.k ?? NaN, 1)}</span>
                        <span style={{color: '#555'}}> </span>
                        <span style={{color: kdjSet.D}}>D:{formatNumber(pt.d ?? NaN, 1)}</span>
                        <span style={{color: '#555'}}> </span>
                        <span style={{color: kdjSet.J}}>J:{formatNumber(pt.j ?? NaN, 1)}</span>
                    </div>
                );
            }
        });

        if (rows.length === 0) return null;
        return (
            <>
                <div style={{borderTop: '1px solid #444', marginTop: 4, paddingTop: 4}}/>
                {rows}
            </>
        );
    };

    // 格式化时间
    const formatTime = (timestamp: number): string => {
        const timeInSeconds = Math.floor(timestamp / 1000);
        if (timeFormatterMap) {
            const mappedLabel = timeFormatterMap[timeInSeconds] || timeFormatterMap[Math.floor(timestamp)];
            if (mappedLabel) {
                return mappedLabel;
            }
        }

        const date = new Date(timestamp);
        const year = String(date.getFullYear()).slice(-2);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        if (timeFrame === '1m' || timeFrame === '5m') {
            return `${year}-${month}-${day} ${hours}:${minutes}`;
        } else if (timeFrame === '1h' || timeFrame === '4h') {
            return `${year}-${month}-${day} ${hours}:00`;
        } else {
            return `${year}-${month}-${day}`;
        }
    };

    // 当没有传入 height prop 时，使用 100% 自适应父容器
    const containerHeight = height ? chartHeight : '100%';

    return (
        <div style={{position: 'relative', width: '100%', height: containerHeight}}>
            <div
                ref={containerRef}
                style={{
                    width: '100%',
                    height: containerHeight,
                    display: loading ? 'none' : 'block'
                }}
            />
            {/* 止盈止损区域背景覆盖层 */}
            <canvas
                ref={overlayCanvasRef}
                width={(width || containerSize.width) * (window.devicePixelRatio || 1)}
                height={chartHeight * (window.devicePixelRatio || 1)}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 1
                }}
            />
            {/* 条件命中高亮覆盖层（z-index 需高于 lightweight-charts 内部画布 1/2，否则会被 K 线盖住） */}
            <canvas
                ref={highlightCanvasRef}
                width={(width || containerSize.width) * (window.devicePixelRatio || 1)}
                height={chartHeight * (window.devicePixelRatio || 1)}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 2
                }}
            />
            {tooltipData && tooltipPosition && (
                <div
                    ref={tooltipRef}
                    style={{
                        position: 'absolute',
                        left: isRightSide
                            ? Math.max(tooltipPosition.x - (tooltipRef.current?.offsetWidth || 200) - 10, 5)
                            : Math.min(tooltipPosition.x + 15, (width || containerSize.width) - 160),
                        top: Math.max(tooltipPosition.y - 100, 10),
                        backgroundColor: 'rgba(30, 30, 30, 0.95)',
                        border: '1px solid #444',
                        borderRadius: 4,
                        padding: '8px 12px',
                        fontSize: 12,
                        color: '#fff',
                        pointerEvents: 'none',
                        zIndex: 1000,
                        minWidth: 140,
                    }}
                >
                    <div style={{color: '#999', marginBottom: 4}}>{formatTime(tooltipData.time)}</div>
                    {tooltipData.open != null && tooltipData.high != null && tooltipData.low != null && tooltipData.close != null && (
                        <>
                            <div style={{display: 'flex', justifyContent: 'space-between', gap: 16}}>
                                <span style={{color: '#999'}}>开:</span>
                                <span>{formatNumber(tooltipData.open)}</span>
                            </div>
                            <div style={{display: 'flex', justifyContent: 'space-between', gap: 16}}>
                                <span style={{color: '#999'}}>高:</span>
                                <span style={{color: '#00d97e'}}>{formatNumber(tooltipData.high)}</span>
                            </div>
                            <div style={{display: 'flex', justifyContent: 'space-between', gap: 16}}>
                                <span style={{color: '#999'}}>低:</span>
                                <span style={{color: '#ff5252'}}>{formatNumber(tooltipData.low)}</span>
                            </div>
                            <div style={{display: 'flex', justifyContent: 'space-between', gap: 16}}>
                                <span style={{color: '#999'}}>收:</span>
                                <span style={{color: tooltipData.close >= tooltipData.open ? '#00d97e' : '#ff5252'}}>
                                    {formatNumber(tooltipData.close)}
                                </span>
                            </div>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: 16,
                                marginTop: 4,
                                borderTop: '1px solid #444',
                                paddingTop: 4
                            }}>
                                <span style={{color: '#999'}}>量:</span>
                                <span>{(tooltipData.volume ?? 0) > 0 ? formatNumber(tooltipData.volume ?? 0, 0) : '-'}</span>
                            </div>
                        </>
                    )}
                    {/* 预测批次数据 */}
                    {tooltipData.batches?.length ? (
                        <div style={{marginTop: 4, borderTop: '1px solid #444', paddingTop: 4}}>
                            {tooltipData.batches.map(b => (
                                <div key={b.id} style={{display: 'flex', justifyContent: 'space-between', gap: 16}}>
                                    <span style={{color: b.color}}>{comparisonBatchLabels?.[b.id] ?? `#${b.id}`}</span>
                                    <span>
                                        O {formatNumber(b.open)} H {formatNumber(b.high)} L {formatNumber(b.low)} C {formatNumber(b.close)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    ) : null}
                    {/* 已勾选的技术指标数值 */}
                    {renderIndicatorRows()}
                </div>
            )}
        </div>
    );
};

export default LightweightCandlestickChart;
